/* ==========================================================================
   Poolform Wizard
   Handles question state, persistence, conditional product filtering,
   recommended-qty math, per-product add-to-cart, and the sticky cart panel.

   Forked from countertop-wizard.js. The cart engine, head-guard integration,
   mini-cart, and rendering are product-agnostic and carry over unchanged; only
   the localStorage namespace (pf_*) and the domain logic differ. All four pool
   questions live in one card at the top of the page; products are assigned to
   sections by source collection (a wizardSection key in the JSON blob) instead
   of wizard:* tags.
   ========================================================================== */
(function () {
  'use strict';

  const root = document.getElementById('ctw-root');
  if (!root) return;

  // -------------------- Product data + constants --------------------
  const PRODUCTS = (function () {
    try {
      const raw = JSON.parse(document.getElementById('ctw-products-data').textContent || '[]');
      // Dedupe — a product may be emitted from several source collections
      // (e.g., Receiver Track lives in the Pool Coping Forms collection but
      // belongs to the Track section). Merge the wizardSection keys so one
      // product can belong to several sections.
      const seen = new Map();
      raw.forEach(p => {
        const existing = seen.get(p.id);
        if (existing) {
          if (p.wizardSection && existing.sections.indexOf(p.wizardSection) === -1) {
            existing.sections.push(p.wizardSection);
          }
          return;
        }
        p.sections = p.wizardSection ? [p.wizardSection] : [];
        seen.set(p.id, p);
      });
      return Array.from(seen.values());
    } catch (e) { console.error('[CTW] Failed to parse product data', e); return []; }
  })();

  const STORAGE_KEY = 'pf_state_v1';
  const SECTIONS = ['forms', 'track', 'liners', 'accessories', 'trowels', 'skimmer', 'release', 'stamps', 'skins', 'plastiforms'];

  // -------------------- Poolform domain constants --------------------
  // No multi-variant (checkbox) products in the Poolform wizard; kept (empty)
  // for the shared card renderer.
  const MULTI_VARIANT_DEFAULTS = {};

  // Per-product custom "Recommended" label override (replaces the default
  // "Recommended: N"). Values may be strings or functions (called with
  // (product, section)) for quantity-dependent labels.
  const RECOMMENDED_LABEL_OVERRIDES = {
    'z-poolform': straightFormLabel,
    'bendable-z-poolform': bendableFormLabel,
  };

  // Product handles the recommendation logic keys off. Everything else in a
  // section is shown as-is (browse) with no quantity math.
  const PF = {
    FORM_STRAIGHT: 'z-poolform',                    // PF-001-S / PF-002-S
    FORM_BENDABLE: 'bendable-z-poolform',           // BPF-001-S / BPF-002-S
    FORM_AUTOCOVER: 'autocover-poolform',           // ACF / CPF / AMF variants
    TRACK_RECEIVER: 'receiver-track',               // RT-001-S(G) — fiberglass + gunite
    TRACK_RECEIVER_BEND: 'bendable-receiver-track', // BRT-001-S(G)
    TRACK_LINER: 'liner-track',                     // LT-001 (96") / LT-001-S (90")
    TRACK_LINER_BEND: 'bendable-liner-track',       // BLT-001
    TRACK_HZ_STRAIGHT: 'hz-straight-liner-track',   // HZW-S08X01 — 8 ft pieces
    TRACK_HZ_NOTCHED: 'hz-notched-liner-track',     // HZW-N08X01 — 8 ft pieces
    LINER_SQUARE_BACKING: 'square-edge-poolform-backing', // SPB-001-S — 60 LF box
    ACC_EXTRACTOR: 'poolform-extraction-tool',      // PET-001
    ACC_MOUNT_TAPE: '2-polyester-mounting-tape',    // PMT-200
    ACC_SCREWS: '1-construction-screw',             // CSC-010
    ACC_VINYL_TAPE: '2-5-white-vinyl-tape',         // WVT-250
    ACC_MALLET: 'poolform-mallet',                  // PFM-001
    ACC_TRIM: 'receiver-trim',                      // TP-001-WHI/GRA/TAN
    RELEASE_1GAL: 'liquid-release-1-gal',           // ZR-004
    TROWEL_CHROME: 'chrome-pool-trowel',            // No-Burn Chrome Pool Trowel
    PLASTI_BOARD_8: 'plastiform-plastic-form-boards',        // TPC-358, 8 ft board
    PLASTI_BOARD_12: 'copy-of-plastiform-plastic-form-boards', // TPC-3512, 12 ft, pickup only
    PLASTI_RODS: 'plastiform-connector-rods',       // PFCR-001, 2 per joint
    PLASTI_CLAMPS: 'plastiform-camlock-clamp',      // PFCL-001, every ~3 ft
  };

  // Hidden from every section regardless of source collection.
  const HIDDEN_HANDLES = ['poolform-samples', 'auto-cover-spacers', 'fritz-pak-mini-delayed-set'];

  // Concrete stamp style buckets, matched against product titles. Order
  // matters: first match wins ("Ashler Notched Slate" → Ashler, not Slate;
  // tampers/bangers are tools and are shown with every style).
  const STAMP_STYLES = [
    { key: 'tools',       label: 'Tampers & Tools', pattern: /tamper|banger/i },
    { key: 'ashler',      label: 'Ashler',          pattern: /ashler/i },
    { key: 'romanfan',    label: 'Roman Fan',       pattern: /roman\s*fan/i },
    { key: 'cobble',      label: 'Cobble',          pattern: /cobble/i },
    { key: 'herringbone', label: 'Herringbone',     pattern: /herringbone/i },
    { key: 'random',      label: 'Random Stone',    pattern: /random/i },
    { key: 'brick',       label: 'Brick',           pattern: /brick/i },
    { key: 'plank',       label: 'Wood Plank',      pattern: /plank|wood/i },
    { key: 'quarry',      label: 'Quarry Stone',    pattern: /quarry/i },
    { key: 'slate',       label: 'Slate',           pattern: /slate/i },
  ];

  // Curated sections are limited to these handles; other sections show every
  // product their source collection provides.
  const SECTION_HANDLE_WHITELIST = {
    forms: [PF.FORM_STRAIGHT, PF.FORM_BENDABLE, PF.FORM_AUTOCOVER],
    track: [PF.TRACK_RECEIVER, PF.TRACK_RECEIVER_BEND, PF.TRACK_LINER, PF.TRACK_LINER_BEND, PF.TRACK_HZ_STRAIGHT, PF.TRACK_HZ_NOTCHED],
  };

  // Coverage / quantity rules
  const COVERAGE = {
    formFullLF: 60,         // Full Package — 8 pieces
    formHalfLF: 30,         // Half Package — 4 pieces
    linerPieceLF: 8,        // every textured form liner is an 8 ft piece
    linerOveragePieces: 1,  // always order one extra piece beyond exact coverage
    squareBackingBoxLF: 60, // Square Edge Poolform Backing box
    hzPieceLF: 8,           // HZ liner track pieces
    trimQty: 2,             // receiver trim pieces per job
    plastiBoardLF: 8,       // 8 ft Plastiform board (TPC-358; 12 ft is pickup-only)
    plastiRodsPerJoint: 2,  // two connector rods per board-to-board joint
    plastiClampSpacingFt: 3 // camlock every 2.5–4 ft → ~3-4 per 8 ft board
  };
  const AC_BRAND_LABELS = { coverstar: 'Coverstar / APC / Integra', coverpools: 'Cover-Pools', aquamatic: 'Aquamatic' };
  const AC_BRAND_PATTERNS = { coverstar: /coverstar|apc|integra/i, coverpools: /cover-?pools/i, aquamatic: /aquamatic/i };

  // -------------------- Utility helpers --------------------
  const $ = (sel, parent) => (parent || root).querySelector(sel);
  const $$ = (sel, parent) => Array.from((parent || root).querySelectorAll(sel));

  function money(cents) {
    if (cents == null || isNaN(cents)) return '';
    return (window.Shopify && Shopify.formatMoney)
      ? Shopify.formatMoney(cents, (window.theme && theme.moneyFormat) || '${{amount}}')
      : '$' + (cents / 100).toFixed(2);
  }
  function escapeHtml(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'})[c]); }
  function debounce(fn, ms) { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; }
  function num(v) { const n = parseFloat(v); return isNaN(n) ? 0 : n; }

  // Out-of-stock products are intentionally NOT filtered out: a wizard-curated
  // product that goes out of stock still appears (with the card's "Out of stock"
  // state + disabled Add button) instead of silently vanishing from its section.
  function byTag(tag) { return PRODUCTS.filter(p => p.tags && p.tags.indexOf(tag) !== -1); }
  function findByHandle(handle) { return PRODUCTS.find(p => p.handle === handle); }
  function findByTagSingle(tag) { return PRODUCTS.find(p => p.tags && p.tags.indexOf(tag) !== -1); }

  // -------------------- State + persistence --------------------
  const State = {
    answers: {
      poolType: '', autoCover: '', acBrand: '',
      copingFt: 0, poolShape: '',
      needFinishingTools: '', needStamps: '', stampStyle: '',
      needSkins: '', needPlastiform: '', plastiformFt: 0
    },
    // cart = the MERGED view the wizard renders: authoritative server cart +
    // the local pending (not-yet-synced) picks overlaid. serverCart = the last
    // /cart.js payload, untouched. See recomputeMergedCart().
    cart: { items: [], total_price: 0, item_count: 0 },
    serverCart: { items: [], total_price: 0, item_count: 0 },
  };

  function loadState() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        Object.assign(State.answers, parsed.answers || {});
      }
    } catch (e) { /* ignore */ }
  }
  function saveState() {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ answers: State.answers })); }
    catch (e) { /* quota/disabled */ }
  }
  function clearState() {
    try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
    Object.assign(State.answers, {
      poolType: '', autoCover: '', acBrand: '',
      copingFt: 0, poolShape: '',
      needFinishingTools: '', needStamps: '', stampStyle: '',
      needSkins: '', needPlastiform: '', plastiformFt: 0
    });
  }

  // -------------------- Cart API --------------------
  // Route every wizard cart call through the pristine native fetch the head
  // guard (snippets/ctw-cart-guard.liquid) exposes as window.__ctwFetch. That
  // keeps our calls out of the guard's third-party throttle and immune to any
  // app's fetch wrapper. Absolute URLs, always.
  // Once Shopify's Cloudflare "managed challenge" fires (HTTP 403 +
  // `cf-mitigated: challenge`), the ENTIRE cart-API session is locked until a
  // top-level navigation issues a fresh cf_clearance cookie. fetch/XHR cannot
  // solve that challenge, so every further add/remove/qty call would also 403
  // and only keep us locked longer. The moment we detect it ANYWHERE, we flip a
  // global lockout: stop firing cart requests entirely and surface the
  // "refresh to continue" banner. This is why, until the customer refreshes,
  // nothing cart-related works — we now make that explicit instead of silent.
  let _cartLockedOut = false;
  // The challenge has been observed as 403 AND as 429 — always with the
  // `cf-mitigated: challenge` response header, which is the reliable signal.
  function isChallengeResponse(r) {
    return !!r && (r.status === 403 || (r.headers && r.headers.get && r.headers.get('cf-mitigated') === 'challenge'));
  }
  function lockOutCart() {
    if (_cartLockedOut) return;
    _cartLockedOut = true;
    persistPendingAdds(); // snapshot unconfirmed adds so the refresh restores them
    showRateLimitBanner();
  }
  function challengeError() {
    const e = new Error('Shopify temporary rate limit'); e.code = 'CHALLENGE'; return e;
  }
  function cartFetch(url, opts) {
    if (_cartLockedOut) return Promise.reject(challengeError());
    const f = (typeof window !== 'undefined' && window.__ctwFetch) ? window.__ctwFetch : fetch;
    const abs = url.charAt(0) === '/' ? (window.location.origin + url) : url;
    return Promise.resolve(f(abs, opts)).then((res) => {
      if (isChallengeResponse(res)) { lockOutCart(); throw challengeError(); }
      return res;
    });
  }
  async function fetchCart() {
    try {
      const res = await cartFetch('/cart.js', { headers: { Accept: 'application/json', 'X-Ctw-Internal': '1' }, credentials: 'same-origin' });
      State.serverCart = await res.json();
      recomputeMergedCart();
    } catch (e) { console.error('[CTW] fetchCart failed', e); }
  }
  // Batched add: a SINGLE POST that adds one or more line items at once.
  // Using the batched format (items: [...]) instead of N separate POSTs
  // avoids tripping Cloudflare's bot-detection rules on the storefront.
  async function addItemsToCart(items, opts) {
    opts = opts || {};
    // Use FormData (multipart/form-data) — closer to the pattern Cloudflare
    // expects from a real browser form submission, less likely to trigger
    // bot-challenge rules than JSON.
    const fd = new FormData();
    items.forEach((it, i) => {
      fd.append(`items[${i}][id]`, it.id);
      fd.append(`items[${i}][quantity]`, it.quantity);
      // Hidden source marker on every wizard line item: a leading-underscore
      // line-item property is saved on the order line but hidden from the
      // customer (cart, mini-cart, checkout, emails). A Shopify Flow tags the
      // order "Poolform Wizard" when any line has this key. Distinct from the
      // Countertop wizard's _ctw so each wizard gets its own order tag.
      fd.append(`items[${i}][properties][_pfw]`, '1');
    });
    // X-Ctw-Internal: marks this as a wizard call so the head guard's throttle
    // passes it straight through to the native fetch.
    const headers = { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json', 'X-Ctw-Internal': '1' };
    // cartFetch throws a CHALLENGE error the instant Cloudflare's managed
    // challenge appears (and short-circuits once locked out), so we never need
    // to inspect the response for it here.
    const doFetch = () => cartFetch('/cart/add.js', { method: 'POST', headers, body: fd, credentials: 'same-origin', keepalive: !!opts.keepalive });
    let res = await doFetch();
    // Retry once on transient failures (network blip / cart session race / soft
    // rate limit). Wait long enough that the retry can't pile onto the same
    // rate-limit window that failed the first attempt.
    if (!res.ok && (res.status === 0 || res.status === 408 || res.status === 429 || res.status >= 500)) {
      await new Promise(r => setTimeout(r, 2500));
      res = await doFetch();
    }
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const msg = err.description || err.message || `Status ${res.status}${res.statusText ? ' ' + res.statusText : ''}`;
      console.error('[CTW] addItems failed', { items, status: res.status, body: err });
      throw new Error(msg);
    }
    return res.json();
  }

  // Back-compat single-item helper — now defers like every other add.
  function addToCart(variantId, quantity) {
    enqueueAdd([{ id: variantId, quantity: quantity }]);
    return Promise.resolve();
  }
  async function changeLine(line, quantity) {
    const res = await cartFetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json', 'X-Ctw-Internal': '1' },
      body: JSON.stringify({ line: line, quantity: quantity }),
    });
    State.serverCart = await res.json();
    recomputeMergedCart();
  }
  async function clearCart() {
    const res = await cartFetch('/cart/clear.js', {
      method: 'POST', credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json', 'X-Ctw-Internal': '1' },
    });
    State.serverCart = await res.json();
    recomputeMergedCart();
  }

  // Rebuild the merged cart view the wizard renders: the authoritative server
  // cart plus the local pending picks (the deferred-add queue) overlaid on top.
  // Pure recompute — never mutates State.serverCart — so it can run after any
  // server fetch OR any local queue change and the panel always shows
  // "what the cart WILL contain", which is what the customer expects to see.
  function recomputeMergedCart() {
    const base = State.serverCart || { items: [] };
    const items = (base.items || []).map(i => Object.assign({}, i));
    _addQueue.forEach(it => {
      const ex = items.find(i => i.id === it.id);
      if (ex) {
        const unit = ex.quantity ? (ex.final_line_price || ex.line_price || 0) / ex.quantity : 0;
        ex.quantity += it.quantity;
        ex.line_price = unit * ex.quantity;
        ex.final_line_price = unit * ex.quantity;
      } else {
        const product = PRODUCTS.find(p => p.variants.some(v => v.id === it.id));
        const variant = product && product.variants.find(v => v.id === it.id);
        if (!product || !variant) return;
        items.push({
          id: it.id,
          product_id: product.id,
          quantity: it.quantity,
          product_title: product.title,
          variant_title: variant.title,
          sku: variant.sku,
          image: variant.image || product.image,
          line_price: variant.price * it.quantity,
          final_line_price: variant.price * it.quantity,
        });
      }
    });
    State.cart = Object.assign({}, base, {
      items: items,
      total_price: items.reduce((s, i) => s + (i.final_line_price || i.line_price || 0), 0),
      item_count: items.reduce((s, i) => s + i.quantity, 0),
    });
  }

  // -------------------- Deferred add queue (local-first cart) --------------------
  // THE core design: while the customer is on the wizard page, "adding to cart"
  // is a purely LOCAL operation. Picks accumulate in _addQueue, render instantly
  // in the wizard panel (merged over the server cart), and persist to
  // localStorage. The real Shopify cart is only written at SYNC POINTS:
  //   1. clicking a cart / checkout link (flush, then navigate),
  //   2. clicking the header cart icon (flush, then open the drawer),
  //   3. leaving / refreshing the page (pagehide → one keepalive POST).
  // A whole wizard session therefore costs ONE batched /cart/add.js write —
  // which makes the Cloudflare cart-API rate limit (the 403/429 "managed
  // challenge" lockout) physically impossible to trip from adding products.
  let _addQueue = [];        // pending [{ id, quantity }]
  let _drainPromise = null;  // Promise of the running flush worker, or null
  let _inFlightBatch = null; // batch currently in a POST (for pagehide snapshot)
  const WRITE_GAP = 1500;    // ms between batches if a flush ever needs several

  // ---------- Pending-pick persistence (survives refresh / navigation) ----------
  // The queue is intentionally long-lived, so it ALWAYS lives in localStorage
  // too. On pagehide we mark a "flush attempt" with a snapshot of the server
  // cart's per-variant counts; on the next load we compare the fresh server
  // cart against that snapshot to tell which items actually landed (drop them)
  // and which didn't (keep them pending). That makes the unload-time keepalive
  // POST safe: it can neither lose items NOR double-add them.
  const PENDING_KEY = 'pf_pending_adds_v1';
  const RESUME_NAV_KEY = 'pf_resume_nav_v1';
  function persistPendingAdds() {
    try {
      if (_addQueue.length) {
        localStorage.setItem(PENDING_KEY, JSON.stringify({ v: 2, items: _addQueue, flushedAt: null }));
      } else {
        localStorage.removeItem(PENDING_KEY);
      }
    } catch (e) { /* storage unavailable — nothing we can do */ }
  }
  function serverVariantCounts() {
    const counts = {};
    ((State.serverCart && State.serverCart.items) || []).forEach(i => {
      counts[i.id] = (counts[i.id] || 0) + i.quantity;
    });
    return counts;
  }
  function markFlushAttempt(items) {
    try {
      localStorage.setItem(PENDING_KEY, JSON.stringify({
        v: 2, items: items, flushedAt: Date.now(), serverCounts: serverVariantCounts()
      }));
    } catch (e) { /* ignore */ }
  }
  function loadPersistedPending() {
    try {
      const raw = localStorage.getItem(PENDING_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      const arr = Array.isArray(data) ? data : (data && Array.isArray(data.items) ? data.items : null);
      if (!arr) { localStorage.removeItem(PENDING_KEY); return null; }
      const items = arr.filter(i => i && i.id && i.quantity > 0);
      if (!items.length) { localStorage.removeItem(PENDING_KEY); return null; }
      return {
        items: items,
        flushedAt: (data && !Array.isArray(data) && data.flushedAt) || null,
        serverCounts: (data && !Array.isArray(data) && data.serverCounts) || {},
      };
    } catch (e) { return null; }
  }
  function setResumeNav(href) {
    try {
      localStorage.setItem(RESUME_NAV_KEY, JSON.stringify({ href: href, t: Date.now() }));
    } catch (e) { /* ignore */ }
  }
  function takeResumeNav() {
    try {
      const raw = localStorage.getItem(RESUME_NAV_KEY);
      localStorage.removeItem(RESUME_NAV_KEY); // consume exactly once
      if (!raw) return null;
      const v = JSON.parse(raw);
      // Only honor a FRESH resume request (the reload happens within seconds).
      // A stale key — e.g. from an interrupted recovery days earlier — must
      // never hijack a later visit and bounce it to the cart.
      if (!v || !v.href || !v.t || (Date.now() - v.t) > 2 * 60 * 1000) return null;
      return v.href;
    } catch (e) { return null; }
  }

  // True while there are picks that have NOT yet been written to the server
  // (queued or mid-flush). Used to decide whether a sync point needs to flush
  // before letting the customer see the real cart.
  function cartWorkPending() {
    return _addQueue.length > 0 || _drainPromise !== null;
  }
  // True only while a flush POST is actually running — used to gate external
  // cart-event re-syncs (the queue itself is long-lived now, so it must NOT
  // block them the way it gated things in the old write-per-add model).
  function flushInProgress() {
    return _drainPromise !== null;
  }

  // Tell the theme to re-fetch and re-render its mini-cart / cart drawer from
  // the (now updated) server cart. This is what keeps the site's cart drawer in
  // sync with the wizard panel. It's a section-render GET, fired at most once
  // per coalesced flush, so it does not re-introduce the rate-limit problem.
  // When WE refresh the drawer, the theme responds by firing `cart:rerendered`.
  // We must NOT treat that as an external change and fire yet another /cart.js
  // reconcile — our own flow already reconciles. Mark a short suppression window
  // so the cart:rerendered listener ignores the echo of our own refresh.
  let _suppressRerenderUntil = 0;
  let _themeRefreshTimer = null;
  // Debounced so a burst of adds wakes the theme drawer / any cart-mirror app
  // (Experro et al.) ONCE instead of per-add. Each wake triggers an expensive
  // /cart?section_id render, which is the main remaining rate-limit pressure.
  function refreshThemeCartNow(immediate) {
    if (_cartLockedOut) return; // a section-render GET would only feed the 429 storm
    _suppressRerenderUntil = Date.now() + 2500;
    if (immediate) {
      // Drawer is about to be shown — re-render it right now, not on a delay.
      if (_themeRefreshTimer) { clearTimeout(_themeRefreshTimer); _themeRefreshTimer = null; }
      root.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { scrollToTop: false }
      }));
      return;
    }
    if (_themeRefreshTimer) return; // a refresh is already scheduled
    _themeRefreshTimer = setTimeout(() => {
      _themeRefreshTimer = null;
      if (_cartLockedOut) return;
      _suppressRerenderUntil = Date.now() + 2500;
      root.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { scrollToTop: false }
      }));
    }, 700);
  }

  // Adding is now a purely LOCAL operation: merge into the queue, persist,
  // re-render. No network, no timers — the server is only written at a sync
  // point (cart/checkout click, drawer open, or page leave).
  function enqueueAdd(items) {
    items.forEach(it => {
      const ex = _addQueue.find(q => q.id === it.id);
      if (ex) ex.quantity += it.quantity;
      else _addQueue.push({ id: it.id, quantity: it.quantity });
    });
    persistPendingAdds();
    recomputeMergedCart();
    renderCartPanel();
    renderAllSections(); // downstream sections may depend on form type, etc.
  }

  // Start the flush worker if it isn't already running. Returns the promise of
  // the running worker so sync points can await a fully-written cart.
  function startDrain(opts) {
    if (_cartLockedOut) { persistPendingAdds(); return Promise.resolve(); }
    if (_drainPromise) return _drainPromise; // already flushing — it will pick up new items
    if (!_addQueue.length) return Promise.resolve();
    _drainPromise = drainQueue(opts).finally(() => { _drainPromise = null; });
    return _drainPromise;
  }

  // Flush the queue ONE batch at a time (normally exactly one POST for the
  // whole wizard session), spaced if a flush ever needs several batches.
  // After the queue empties, refresh the theme drawer ONCE and reconcile ONCE
  // against the authoritative server cart.
  async function drainQueue(opts) {
    let lockedOut = false;
    let first = true;
    while (_addQueue.length) {
      if (_cartLockedOut) { persistPendingAdds(); lockedOut = true; break; }
      if (!first) await new Promise(r => setTimeout(r, WRITE_GAP));
      first = false;
      const batch = _addQueue;
      _addQueue = [];
      _inFlightBatch = batch;
      try {
        await addItemsToCart(batch, opts);
        _inFlightBatch = null;
        persistPendingAdds(); // queue shrank — keep the snapshot accurate (clears when empty)
      } catch (err) {
        _inFlightBatch = null;
        console.error('[CTW] batched flush failed', err);
        handleCartError(err);
        if (err && err.code === 'CHALLENGE') {
          // Locked out by the Cloudflare challenge mid-batch. Put the
          // unconfirmed batch BACK in the queue and persist it — picks stay
          // local and the next sync point (after the refresh that solves the
          // challenge) writes them. Nothing is ever silently dropped.
          _addQueue = batch.concat(_addQueue);
          persistPendingAdds();
          lockedOut = true;
          break;
        }
        // Genuine failure (bad variant, out of stock...): abandon the rest of
        // the queue and fall through to the reconcile below, which pulls
        // authoritative server state and drops any phantom items.
        _addQueue = [];
        persistPendingAdds();
        break;
      }
    }
    if (lockedOut) return;
    // Queue fully flushed — ONE drawer re-render for the whole run, then
    // reconcile the panel against the real cart (applies discounts, fixes
    // ordering, removes phantoms).
    refreshThemeCartNow();
    try {
      await fetchCart();
      renderCartPanel();
      renderAllSections();
    } catch (e) { /* non-fatal */ }
  }

  // Drain the queue and wait for the worker so the server cart is fully up to
  // date. Resolves even on error so navigation is never permanently blocked.
  async function settleCartAdds() {
    try { await startDrain(); } catch (e) { /* ignore */ }
    if (_drainPromise) { try { await _drainPromise; } catch (e) { /* ignore */ } }
  }

  // Discard any pending adds (used by Start Over so a late flush can't
  // re-populate a cart we just cleared). The in-flight worker, if any, is
  // awaited separately by the caller.
  function cancelPendingAdds() {
    _addQueue = [];
    persistPendingAdds(); // queue is now empty — clear any persisted snapshot too
  }

  // -------------------- Sync points --------------------
  // The deferred queue is written to the real cart at exactly these moments:
  //   pagehide        → one keepalive POST (covers refresh, back, any link out)
  //   cart drawer     → flush, then open the drawer showing the true cart
  //   cart / checkout → flush, then navigate
  function bindFlushBeforeUnload() {
    // Leaving the page (refresh, navigation, close). Mark the flush attempt
    // FIRST — with a snapshot of the server cart's per-variant counts — then
    // fire the keepalive POST. On the next load, init() compares the fresh
    // server cart against the snapshot: items that landed are dropped, items
    // that didn't stay pending. Safe against both loss AND double-adds.
    window.addEventListener('pagehide', () => {
      const combined = (_inFlightBatch || []).concat(_addQueue);
      if (!combined.length) return;
      markFlushAttempt(combined);
      if (!_drainPromise && !_cartLockedOut) startDrain({ keepalive: true });
    });

    const CART_NAV = /\/(cart|checkout)(\/|\?|#|$)/i;
    // Persist pending picks + remember the destination, then reload. The reload
    // (a top-level navigation) solves the Cloudflare challenge; init() restores
    // the picks, flushes them, and continues to the destination — the customer
    // still ends up where they clicked, WITH all their items.
    function recoverThenNavigate(href) {
      persistPendingAdds();
      setResumeNav(href || '/checkout');
      hardReload();
    }
    // Re-dispatch marker so our own programmatic click on the drawer toggle
    // isn't intercepted again.
    let _passThroughClick = false;
    // Capture phase so we run before the theme's own cart / checkout handlers.
    document.addEventListener('click', (e) => {
      if (_passThroughClick) return;
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      // Header cart icon: the theme turns this <a href="/cart"> into a drawer
      // toggle. Sync first so the drawer always shows the TRUE cart, then let
      // the theme open it. This is the one place mid-wizard where the customer
      // can see the system cart — and now it's always accurate there.
      const toggle = e.target.closest('[data-action="toggle-mini-cart"]');
      if (toggle) {
        if (!cartWorkPending()) return; // already in sync — open normally
        e.preventDefault();
        e.stopPropagation();
        settleCartAdds().then(() => {
          if (_cartLockedOut && _addQueue.length) return; // banner is up; don't show a stale drawer
          refreshThemeCartNow(true); // immediate re-render with the fresh cart
          _passThroughClick = true;
          try { toggle.click(); } finally { _passThroughClick = false; }
        });
        return;
      }

      const a = e.target.closest('a[href]');
      const checkoutBtn = e.target.closest('button[name="checkout"], [data-action="checkout"]');
      let href = null;
      if (a && CART_NAV.test(a.getAttribute('href') || '')) href = a.href;
      if (!href && !checkoutBtn) return;
      if (_cartLockedOut) {
        if (_addQueue.length) {
          // Going to the cart now would show a cart MISSING what the customer
          // sees in the wizard panel — the exact confusion we're fixing.
          e.preventDefault();
          e.stopPropagation();
          recoverThenNavigate(href);
        }
        // Nothing pending: let the navigation proceed — being a top-level
        // navigation, it solves the challenge by itself.
        return;
      }
      if (!cartWorkPending()) return; // server already in sync — let it proceed normally
      e.preventDefault();
      e.stopPropagation();
      settleCartAdds().finally(() => {
        if (_cartLockedOut && _addQueue.length) {
          // The flush itself hit the challenge — recover via reload.
          recoverThenNavigate(href);
          return;
        }
        window.location.href = href || '/checkout';
      });
    }, true);
  }

  // -------------------- Rate-limit recovery banner --------------------
  // When Shopify's Cloudflare challenge locks the cart API, fetch can't recover
  // in-page. A full top-level navigation (refresh) solves the challenge and
  // restores cart access. The customer's answers persist in localStorage and the
  // cart reloads from the server, so nothing is lost.
  let _challengeBanner = null;
  function handleCartError(err) {
    if (err && err.code === 'CHALLENGE') showRateLimitBanner();
  }
  // Reload that cannot be swallowed: reload() first, and if anything on the
  // page interfered (SPA routers etc.), force a same-URL navigation as backup.
  function hardReload() {
    try { window.location.reload(); } catch (e) { /* fall through */ }
    setTimeout(() => {
      try { window.location.href = window.location.pathname + window.location.search; } catch (e) { /* ignore */ }
    }, 350);
  }
  function showRateLimitBanner() {
    if (_challengeBanner) {
      // Already visible — pulse it so a blocked action visibly points here.
      _challengeBanner.classList.remove('is-pulse');
      void _challengeBanner.offsetWidth; // restart the CSS animation
      _challengeBanner.classList.add('is-pulse');
      return;
    }
    const bar = document.createElement('div');
    bar.className = 'ctw__ratelimit';
    // data-ctw-reload: also handled by a FIRST-registered capture listener in
    // the head guard (snippets/ctw-cart-guard.liquid), so no third-party click
    // handler can stopPropagation before the reload fires.
    bar.innerHTML =
      '<span>Cart updates are paused by a temporary traffic limit. Everything you’ve added is saved — refresh to apply it to your cart.</span>' +
      '<button type="button" class="ctw__ratelimit-refresh" data-ctw-reload>Refresh</button>';
    bar.querySelector('.ctw__ratelimit-refresh').addEventListener('click', hardReload);
    document.body.appendChild(bar);
    _challengeBanner = bar;
  }

  // -------------------- Derived helpers --------------------
  // The Poolform questions are all up-front, so the logic reads State.answers
  // directly. Cart-aware helpers can be added here as cross-product rules
  // emerge (follow the countertop wizard pattern).

  function copingLF() { return num(State.answers.copingFt); }
  function isAutoCover() { return State.answers.autoCover === 'yes'; }
  function shapeChosen() { return State.answers.poolShape !== ''; }
  // Rounded-corner rectangles and freeform pools both form with the bendable
  // products; square-cornered rectangles (or radius under 6") use straight.
  function needsBendable() {
    return State.answers.poolShape === 'rectround' || State.answers.poolShape === 'freeform';
  }

  // Pack a needed LF into Full (60 LF) / Half (30 LF) packages:
  //   ≤30 ft → 1 Half; 31–60 ft → 1 Full; >60 ft → Fulls + at most one Half
  //   (a remainder over 30 ft bumps to another Full).
  function packForms(lf) {
    if (lf <= 0) return null;
    let full, half;
    if (lf <= COVERAGE.formHalfLF) { full = 0; half = 1; }
    else if (lf <= COVERAGE.formFullLF) { full = 1; half = 0; }
    else {
      full = Math.floor(lf / COVERAGE.formFullLF);
      const remainder = lf - full * COVERAGE.formFullLF;
      if (remainder === 0) half = 0;
      else if (remainder <= COVERAGE.formHalfLF) half = 1;
      else { full += 1; half = 0; }
    }
    return { full, half, capacity: full * COVERAGE.formFullLF + half * COVERAGE.formHalfLF };
  }

  // "2 Full Packages + 1 Half Package" — list of human-readable package counts.
  function packPartsText(full, half) {
    const parts = [];
    if (full > 0) parts.push(full + ' Full Package' + (full > 1 ? 's' : ''));
    if (half > 0) parts.push(half + ' Half Package' + (half > 1 ? 's' : ''));
    return parts;
  }

  // The coping-form purchase plan for the current answers, or null when shape /
  // footage are missing (or the pool has an auto cover — see the AC branch).
  //   rect      → straight packages only (packForms math)
  //   freeform  → bendable packages only (packForms math)
  //   rectround → MIX: straight Fulls for the long walls + ONE bendable package
  //               (Half or Full) for the rounded corners. Tiers per the rules:
  //               ≤90 ft → 1 straight Full + 1 bendable Half (90 ft capacity)
  //               ≤120   → 1 straight Full + 1 bendable Full (120)
  //               ≤150   → 2 straight Fulls + 1 bendable Half (150)
  //               …continuing in 30 ft steps (bendable alternates Half/Full).
  function formPlan() {
    const lf = copingLF();
    if (lf <= 0 || !shapeChosen() || isAutoCover()) return null;
    const shape = State.answers.poolShape;
    if (shape === 'rect') {
      const p = packForms(lf);
      return { straightFull: p.full, straightHalf: p.half, bendFull: 0, bendHalf: 0, capacity: p.capacity, mixed: false };
    }
    if (shape === 'freeform') {
      const p = packForms(lf);
      return { straightFull: 0, straightHalf: 0, bendFull: p.full, bendHalf: p.half, capacity: p.capacity, mixed: false };
    }
    // rectround
    if (lf <= 90) {
      return { straightFull: 1, straightHalf: 0, bendFull: 0, bendHalf: 1, capacity: 90, mixed: true };
    }
    const capacity = Math.ceil((lf - 60) / 30) * 30 + 60;
    const bendFull = ((capacity - 60) / 30) % 2 === 0 ? 1 : 0;
    const bendHalf = bendFull ? 0 : 1;
    const straightFull = (capacity - (bendFull ? 60 : 30)) / 60;
    return { straightFull, straightHalf: 0, bendFull, bendHalf, capacity, mixed: true };
  }

  // Badge label overrides for the two coping-form cards.
  function straightFormLabel() {
    const plan = formPlan();
    if (!plan) return null;
    const parts = packPartsText(plan.straightFull, plan.straightHalf);
    if (!parts.length) return null;
    return 'Recommended: ' + parts.join(' + ') + (plan.mixed ? ' — straight runs' : '');
  }
  function bendableFormLabel() {
    const plan = formPlan();
    if (!plan) return null;
    const parts = packPartsText(plan.bendFull, plan.bendHalf);
    if (!parts.length) return null;
    return 'Recommended: ' + parts.join(' + ') + (plan.mixed ? ' — rounded corners' : '');
  }

  // When a single coping-form product's recommendation is a COMBINATION of both
  // a Full and a Half package (e.g. freeform ~130 ft → 2 Full + 1 Half on the
  // Bendable Z Poolform), the card's single dropdown + qty can only add one
  // variant per click. This resolves that combo so the card/quick-view can offer
  // a one-click "add both lines" button (via enqueueAdd's multi-line path).
  // Returns null for everything else (single-package, exact multiples, non-form
  // products, rectround split, or a missing/sold-out Full or Half variant) so
  // those cards stay exactly as they are today. Variant matching uses the same
  // /full/i and /half/i title test as defaultVariantFor, so the lines enqueued
  // always agree with the recommended-qty prefill and the badge label.
  // Can this variant supply `qty` units? True for untracked / backorder-allowed
  // variants; for inventory-tracked ones, only when enough is in stock.
  function variantCanSupply(v, qty) {
    if (!v) return false;
    if (v.inventoryManagement === 'shopify' && v.inventoryPolicy !== 'continue') {
      return (v.inventoryQuantity || 0) >= qty;
    }
    return true;
  }
  function formComboPlan(product) {
    if (!product) return null;
    if (product.handle !== PF.FORM_STRAIGHT && product.handle !== PF.FORM_BENDABLE) return null;
    const plan = formPlan();
    if (!plan) return null;
    const fullCount = product.handle === PF.FORM_STRAIGHT ? plan.straightFull : plan.bendFull;
    const halfCount = product.handle === PF.FORM_STRAIGHT ? plan.straightHalf : plan.bendHalf;
    if (!(fullCount > 0 && halfCount > 0)) return null;
    const vs = product.variants || [];
    const fullVariant = vs.find(v => /full/i.test(v.title || '') && v.available);
    const halfVariant = vs.find(v => /half/i.test(v.title || '') && v.available);
    if (!fullVariant || !halfVariant) return null;
    // Don't offer a one-click combo that would exceed tracked stock — the combo
    // bypasses the qty input, and an over-qty batched flush is rejected wholesale.
    // Degrade to the manual dropdown (which surfaces a max=) instead.
    if (!variantCanSupply(fullVariant, fullCount) || !variantCanSupply(halfVariant, halfCount)) return null;
    return {
      fullCount, halfCount, fullVariant, halfVariant,
      text: packPartsText(fullCount, halfCount).join(' + '),
    };
  }

  // Which style bucket a concrete stamp product belongs to (first match wins).
  function stampStyleOf(product) {
    const t = product.title || '';
    const s = STAMP_STYLES.find(st => st.pattern.test(t));
    return s ? s.key : 'other';
  }

  // AutoCover Poolform boxes: 90" variants are 8 pieces = 60 LF, 96" variants
  // are 8 pieces = 64 LF. Read the size off the variant title.
  function acBoxLF(variant) {
    return /96/.test((variant && variant.title) || '') ? 64 : 60;
  }

  // Track case footage by variant: 96" pieces → 128 LF/case, 90" → 120 LF/case.
  function trackCaseLF(product, variant) {
    const t = (variant && variant.title) || '';
    if (/96/.test(t)) return 128;
    if (/90/.test(t)) return 120;
    if (product && product.handle === PF.TRACK_LINER_BEND) return 128; // 16 × 96" pieces
    return 120; // receiver tracks: 16 × 90" pieces
  }

  // The variant the card will show: the user's dropdown choice if they made
  // one, else the answer-driven default.
  function selectedVariantFor(product) {
    if (!product || !product.variants || !product.variants.length) return null;
    const userChoiceId = userVariantMap.get(product.id);
    if (userChoiceId) {
      const found = product.variants.find(v => v.id === userChoiceId);
      if (found) return found;
    }
    return defaultVariantFor(product);
  }

  // -------------------- Section product selection (which products to show) --------------------
  // Out-of-stock products are intentionally NOT filtered out: a wizard-curated
  // product that goes out of stock still appears (with the card's "Out of
  // stock" state + disabled Add button) instead of silently vanishing.
  function bySection(key) {
    let list = PRODUCTS.filter(p => p.sections && p.sections.indexOf(key) !== -1);
    list = list.filter(p => HIDDEN_HANDLES.indexOf(p.handle) === -1);
    const whitelist = SECTION_HANDLE_WHITELIST[key];
    if (whitelist) list = list.filter(p => whitelist.indexOf(p.handle) !== -1);
    return list;
  }

  function productsForSection(section) {
    switch (section) {
      case 'forms': {
        const all = bySection('forms');
        // Auto-cover pool: the AutoCover Poolform is the ONLY form — it spans
        // the formed sides; the cover-box side is not formed.
        if (isAutoCover()) return all.filter(p => p.handle === PF.FORM_AUTOCOVER);
        let list = all;
        // Once they've said "no auto cover", hide the AutoCover form.
        if (State.answers.autoCover === 'no') list = list.filter(p => p.handle !== PF.FORM_AUTOCOVER);
        // Shape narrows the form type. Rounded-corner rectangles use BOTH:
        // straight for the walls, bendable for the corners.
        if (State.answers.poolShape === 'rect') list = list.filter(p => p.handle !== PF.FORM_BENDABLE);
        else if (State.answers.poolShape === 'freeform') list = list.filter(p => p.handle !== PF.FORM_STRAIGHT);
        return list;
      }

      case 'track': {
        // Auto-cover pools don't use receiver/liner track at all — the
        // recommendation banner explains the empty section.
        if (isAutoCover()) return [];
        let list = bySection('track');
        const pt = State.answers.poolType;
        const shape = State.answers.poolShape;
        if (pt === 'vinyl') {
          list = list.filter(p => p.handle === PF.TRACK_LINER || p.handle === PF.TRACK_LINER_BEND ||
                                  p.handle === PF.TRACK_HZ_STRAIGHT || p.handle === PF.TRACK_HZ_NOTCHED);
          if (shape === 'freeform') {
            list = list.filter(p => p.handle === PF.TRACK_LINER_BEND || p.handle === PF.TRACK_HZ_NOTCHED);
          } else if (shape === 'rect') {
            list = list.filter(p => p.handle === PF.TRACK_LINER || p.handle === PF.TRACK_HZ_STRAIGHT);
          }
          // rectround keeps all four: straight + bendable can combine.
        } else if (pt === 'fiberglass' || pt === 'gunite') {
          list = list.filter(p => p.handle === PF.TRACK_RECEIVER || p.handle === PF.TRACK_RECEIVER_BEND);
          if (shape === 'freeform') {
            list = list.filter(p => p.handle === PF.TRACK_RECEIVER_BEND);
          } else if (shape === 'rect') {
            list = list.filter(p => p.handle === PF.TRACK_RECEIVER);
          }
          // rectround keeps both.
        }
        return list;
      }

      case 'accessories': {
        let list = bySection('accessories');
        // Auto-cover-only setups need neither the extraction tool (no standard
        // forms to strip) nor the receiver trim (no receiver track to cap).
        if (isAutoCoverOnly()) {
          list = list.filter(p => p.handle !== PF.ACC_EXTRACTOR && p.handle !== PF.ACC_TRIM);
        }
        // Mounting tape exists to stick the receiver track to a fiberglass
        // shell — only show it on fiberglass pools (with no auto cover).
        if (!(State.answers.poolType === 'fiberglass' && !isAutoCover())) {
          list = list.filter(p => p.handle !== PF.ACC_MOUNT_TAPE);
        }
        // Receiver Trim caps the receiver track, so it's irrelevant to liner-track
        // setups: hide it for vinyl liner pools, or whenever the cart has a liner
        // track but no receiver track.
        if (State.answers.poolType === 'vinyl' || (hasLinerTrackInCart() && !hasReceiverTrackInCart())) {
          list = list.filter(p => p.handle !== PF.ACC_TRIM);
        }
        return list;
      }

      case 'trowels':
        return State.answers.needFinishingTools === 'yes' ? bySection('trowels') : [];

      case 'stamps': {
        if (State.answers.needStamps !== 'yes') return [];
        let list = bySection('stamps');
        const style = State.answers.stampStyle;
        if (style && style !== 'all') {
          // Tampers/tools are shown with every style — you need one regardless.
          list = list.filter(p => {
            const k = stampStyleOf(p);
            return k === style || k === 'tools';
          });
        }
        return list;
      }

      case 'skins':
        return State.answers.needSkins === 'yes' ? bySection('skins') : [];

      case 'plastiforms':
        return State.answers.needPlastiform === 'yes' ? bySection('plastiforms') : [];

      // Liners and the remaining browse sections show everything from their
      // source collection — badges and quantities do the narrowing.
      default:
        return bySection(section);
    }
  }

  // -------------------- Recommendation math --------------------
  function recommendForms() {
    const lf = copingLF();
    if (isAutoCover()) {
      const brandLabel = AC_BRAND_LABELS[State.answers.acBrand];
      const lines = [];
      if (lf > 0) {
        const product = findByHandle(PF.FORM_AUTOCOVER);
        const variant = product ? selectedVariantFor(product) : null;
        const boxLF = acBoxLF(variant);
        const boxes = Math.ceil(lf / boxLF);
        lines.push(`Based on <strong>${lf} ft</strong> of coping on an auto-cover pool, you'll need <strong>${boxes} box${boxes > 1 ? 'es' : ''}</strong> of the AutoCover Poolform${brandLabel ? ` for <strong>${brandLabel}</strong>` : ''} (8 pieces / ${boxLF} ft per box — covers up to <strong>${boxes * boxLF} ft</strong>).`);
      } else {
        lines.push("Enter your linear feet of coping above and we'll calculate how many AutoCover Poolform boxes you need.");
      }
      if (!brandLabel) lines.push('Select your <strong>auto cover brand</strong> above so the right form profile is pre-selected.');
      lines.push('On auto-cover pools the AutoCover Poolform is the <strong>only</strong> form you need — it replaces the standard coping form and the receiver/liner track. The side with the cover housing is not formed, so only count the remaining sides in your footage.');
      return { html: lines.join('<br>') };
    }
    if (!shapeChosen() && lf <= 0) return null;
    if (!shapeChosen()) {
      return { html: 'Select your <strong>pool shape</strong> above — square-cornered rectangles use the <strong>Straight Z Poolform</strong>; rounded corners use a mix of straight and bendable; freeform pools use the <strong>Bendable Z Poolform</strong>.' };
    }
    const shape = State.answers.poolShape;
    if (lf <= 0) {
      const msg = shape === 'rect'
        ? 'Your shape calls for the <strong>Straight Z Poolform</strong> — square corners (or a radius under 6") form with straight sections.'
        : shape === 'rectround'
          ? 'With rounded corners the cleanest setup is <strong>straight forms for the long walls</strong> plus a <strong>bendable package for the corners</strong>.'
          : 'A freeform pool needs the <strong>Bendable Z Poolform</strong> to follow the curves.';
      return { html: msg + " Enter your linear feet of coping above and we'll calculate the packages." };
    }
    const plan = formPlan();
    let html;
    if (shape === 'rect') {
      html = `Based on <strong>${lf} ft</strong> of coping, you'll need <strong>${packPartsText(plan.straightFull, plan.straightHalf).join(' + ')}</strong> of the <strong>Z Poolform (straight)</strong> — square corners (or a radius under 6") form with straight sections. A Full Package covers 60 ft (8 pieces); a Half Package covers 30 ft (4 pieces). Yours covers up to <strong>${plan.capacity} ft</strong>.`;
    } else if (shape === 'freeform') {
      html = `Based on <strong>${lf} ft</strong> of coping, you'll need <strong>${packPartsText(plan.bendFull, plan.bendHalf).join(' + ')}</strong> of the <strong>Bendable Z Poolform</strong> — a freeform pool needs the bendable form to follow the curves. A Full Package covers 60 ft (8 pieces); a Half Package covers 30 ft (4 pieces). Yours covers up to <strong>${plan.capacity} ft</strong>.`;
    } else {
      const sParts = packPartsText(plan.straightFull, plan.straightHalf).join(' + ');
      const bParts = packPartsText(plan.bendFull, plan.bendHalf).join(' + ');
      html = `Based on <strong>${lf} ft</strong> of coping with rounded corners, the cleanest setup is <strong>straight forms for the long walls</strong> and <strong>bendable forms just for the corners</strong>: <strong>${sParts}</strong> of the Straight Z Poolform + <strong>${bParts}</strong> of the Bendable Z Poolform — covering up to <strong>${plan.capacity} ft</strong> combined. Bendable forms could do the whole pool, but straight packages handle the long runs cleaner, so we only recommend enough bendable to wrap the corners.`;
    }
    if (plan.capacity - lf <= 5) {
      html += `<div class="ctw-warn">⚠️ <strong>${lf} ft</strong> is cutting it close to the <strong>${plan.capacity} ft</strong> capacity. Consider stepping up a package size so you don't run short.</div>`;
    }
    return { html };
  }

  function recommendTrack() {
    if (isAutoCover()) {
      return { html: 'No receiver or liner track is needed on an auto-cover pool — the AutoCover Poolform anchors directly, so there is nothing to add in this section.' };
    }
    const pt = State.answers.poolType;
    const lf = copingLF();
    if (!pt) return { html: 'Select your <strong>pool type</strong> above — vinyl liner pools use <strong>Liner Track</strong>; fiberglass and concrete/gunite pools use <strong>Receiver Track</strong>.' };
    const shape = State.answers.poolShape;
    const lines = [];
    if (pt === 'vinyl') {
      if (shape === 'rectround') {
        const bendCase = 128;
        if (lf <= 0) {
          lines.push("Vinyl liner pool with rounded corners: enter your linear feet above and we'll work out the track combination.");
        } else if (lf <= bendCase) {
          lines.push(`Vinyl liner pool with rounded corners: <strong>1 case</strong> of <strong>Bendable Liner Track</strong> (16 pieces / ${bendCase} ft) covers your <strong>${lf} ft</strong> — corners and straight runs alike. Track only comes in full cases, so when your footage fits in one case it's cheaper to use a single bendable case than to buy separate straight and bendable cases.`);
        } else {
          const product = findByHandle(PF.TRACK_LINER);
          const caseLF = trackCaseLF(product, product ? selectedVariantFor(product) : null);
          const straightCases = Math.ceil((lf - bendCase) / caseLF);
          lines.push(`Vinyl liner pool with rounded corners over ${bendCase} ft: <strong>1 case of Bendable Liner Track</strong> (${bendCase} ft — use it on the corners and the walls nearest them) + <strong>${straightCases} case${straightCases > 1 ? 's' : ''} of straight Liner Track</strong> (${caseLF} ft per case) for the remaining straight runs. Together they cover your <strong>${lf} ft</strong> with the least waste.`);
        }
      } else {
        const handle = shape === 'freeform' ? PF.TRACK_LINER_BEND : PF.TRACK_LINER;
        const product = findByHandle(handle);
        const variant = product ? selectedVariantFor(product) : null;
        const caseLF = trackCaseLF(product, variant);
        const name = handle === PF.TRACK_LINER_BEND ? 'Bendable Liner Track' : 'Liner Track';
        if (lf > 0) {
          const cases = Math.ceil(lf / caseLF);
          lines.push(`Vinyl liner pool: <strong>${cases} case${cases > 1 ? 's' : ''}</strong> of <strong>${name}</strong> (16 pieces / ${caseLF} ft per case) covers your <strong>${lf} ft</strong>.`);
        } else {
          lines.push(`Vinyl liner pool: you'll need <strong>${name}</strong> — enter your linear feet above and we'll calculate the case count.`);
        }
      }
      const hz = shape === 'freeform' ? 'HZ Notched Liner Track'
               : shape === 'rectround' ? 'HZ tracks (straight for the walls, notched for the corners)'
               : 'HZ Straight Liner Track';
      lines.push(`The <strong>${hz}</strong> (sold per 8 ft piece) ${shape === 'rectround' ? 'are' : 'is'} shown as an alternative if you prefer the HZ system.`);
      lines.push("<strong>What's the difference?</strong> The Liner Track and Bendable Liner Track are <strong>PVC</strong> — they don't need to be bonded and won't corrode in pools with saltwater systems. The HZ tracks are <strong>extruded aluminum</strong>.");
      lines.push('The Z Poolform system is also compatible with many aluminum flat-track liner tracks.');
    } else {
      const label = pt === 'fiberglass' ? 'Fiberglass' : 'Concrete / gunite';
      if (shape === 'rectround') {
        if (lf <= 0) {
          lines.push(`${label} pool with rounded corners: enter your linear feet above and we'll work out the track combination.`);
        } else if (lf <= 120) {
          lines.push(`${label} pool with rounded corners: <strong>1 case</strong> of <strong>Bendable Receiver Track</strong> (16 pieces / 120 ft) covers your <strong>${lf} ft</strong> — corners and straight runs alike. Receiver track only comes in 120 ft cases, so when your footage fits in one case it's cheaper to use a single bendable case than to buy separate straight and bendable cases. Choose <strong>White or Gray</strong>.`);
        } else {
          const straightCases = Math.ceil((lf - 120) / 120);
          lines.push(`${label} pool with rounded corners over 120 ft: <strong>1 case of Bendable Receiver Track</strong> (use it on the corners and the walls nearest them) + <strong>${straightCases} case${straightCases > 1 ? 's' : ''} of straight Receiver Track</strong> for the remaining runs (120 ft per case). Together they cover your <strong>${lf} ft</strong> with the least waste. Choose <strong>White or Gray</strong>.`);
        }
      } else {
        const name = shape === 'freeform' ? 'Bendable Receiver Track' : 'Receiver Track';
        if (lf > 0) {
          const cases = Math.ceil(lf / 120);
          lines.push(`${label} pool: <strong>${cases} case${cases > 1 ? 's' : ''}</strong> of <strong>${name}</strong> (16 pieces / 120 ft per case) covers your <strong>${lf} ft</strong>. Choose <strong>White or Gray</strong> in the dropdown.`);
        } else {
          lines.push(`${label} pool: you'll need <strong>${name}</strong> — enter your linear feet above and we'll calculate the case count. Choose White or Gray in the dropdown.`);
        }
      }
      lines.push("Don't forget the matching <strong>Receiver Trim</strong> in the Accessories section — a beauty trim that hides the gap left by the receiver track.");
    }
    return { html: lines.join('<br>') };
  }

  function recommendLiners() {
    const lf = copingLF();
    if (lf <= 0) return null;
    const pieces = Math.ceil(lf / COVERAGE.linerPieceLF) + COVERAGE.linerOveragePieces;
    const exact = pieces - COVERAGE.linerOveragePieces;
    const backingBoxes = Math.ceil(lf / COVERAGE.squareBackingBoxLF);
    const lines = [
      `Every liner texture below works with your forms — pick the look you want. Whichever you choose, order <strong>${pieces} pieces</strong>: each liner is 8 ft, so ${exact} piece${exact > 1 ? 's' : ''} covers your <strong>${lf} ft</strong> and the extra piece is built-in overage so you don't run short.`,
      `Prefer a smooth square edge with no texture? Use the <strong>Square Edge Poolform Backing</strong> instead — <strong>${backingBoxes} box${backingBoxes > 1 ? 'es' : ''}</strong> (8 pieces / 60 ft per box) covers your footage, and it needs no form liner or liquid release.`
    ];
    return { html: lines.join('<br>') };
  }

  function recommendAccessories() {
    // Auto-cover-only setups: nothing in this section is recommended.
    if (isAutoCoverOnly()) return null;
    const pt = State.answers.poolType;
    if (State.answers.autoCover === '' && !pt) return null;
    const noAC = State.answers.autoCover === 'no';
    const lines = [];
    if (noAC) {
      lines.push('• <strong>Poolform Extraction Tool:</strong> 1 — makes stripping the forms dramatically easier.');
    }
    if (noAC && pt === 'fiberglass') {
      lines.push('• <strong>2" Polyester Mounting Tape:</strong> 1 roll — our recommended way to stick the receiver track down to a fiberglass shell.');
      lines.push('<em>Or fasten the receiver track with <strong>1" Construction Screws</strong> instead — pick <strong>one or the other</strong>, not both. (The screws are shown below but aren\'t pre-selected for fiberglass.)</em>');
    }
    if (noAC && pt === 'gunite') {
      lines.push('• <strong>1" Construction Screws:</strong> 1 box — fastens the receiver track to concrete/gunite.');
    }
    if (noAC && (pt === 'fiberglass' || pt === 'gunite')) {
      lines.push(`• <strong>Receiver Trim:</strong> ${COVERAGE.trimQty} — a beauty trim that hides the gap left by the receiver track. Pick White, Gray, or Tan to match.`);
    }
    if (noAC && needsBendable()) {
      lines.push('• <strong>2.5" White Vinyl Tape:</strong> 1 roll — seals the bendable form face on curves.');
      lines.push('• <strong>Poolform Mallet:</strong> 1 — seats the bendable forms along the radius.');
    }
    if (!lines.length) return null;
    return { html: lines.join('<br>') };
  }

  // Cart-content checks run on the merged view, so they react to local picks too.
  function cartContainsHandle(handles) {
    return (State.cart.items || []).some(item => {
      const p = PRODUCTS.find(pp => pp.id === item.product_id);
      return p && handles.indexOf(p.handle) !== -1;
    });
  }
  function hasLinerTrackInCart() {
    return cartContainsHandle([PF.TRACK_LINER, PF.TRACK_LINER_BEND, PF.TRACK_HZ_STRAIGHT, PF.TRACK_HZ_NOTCHED]);
  }
  function hasReceiverTrackInCart() {
    return cartContainsHandle([PF.TRACK_RECEIVER, PF.TRACK_RECEIVER_BEND]);
  }

  // "Auto-cover-only": the pool has an auto cover (answer = yes), OR the only
  // coping form in the cart is the AutoCover Poolform (no straight/bendable
  // forms). These setups need no extraction tool, receiver trim, or other
  // accessories — the AutoCover form anchors directly.
  function isAutoCoverOnly() {
    if (isAutoCover()) return true;
    return cartContainsHandle([PF.FORM_AUTOCOVER]) &&
           !cartContainsHandle([PF.FORM_STRAIGHT, PF.FORM_BENDABLE]);
  }

  // True if the merged cart contains the Square Edge Poolform Backing — a smooth
  // no-texture edge that needs no form liner and no liquid release.
  function hasSquareBackingInCart() {
    return cartContainsHandle([PF.LINER_SQUARE_BACKING]);
  }

  function recommendRelease() {
    // Square Edge Backing strips clean on its own — no release needed, so don't
    // recommend it once the backing is in the cart.
    if (hasSquareBackingInCart()) {
      return { html: "You've added the <strong>Square Edge Poolform Backing</strong>, which needs no form release — you can skip the liquid release here." };
    }
    const lf = copingLF();
    if (lf <= 0) return null;
    return { html: "Add <strong>1 gallon of Liquid Release</strong> — it's required with any textured form liner so the forms strip cleanly without tearing the texture. (Not needed if you're using only the Square Edge Poolform Backing.)" };
  }

  function recommendTrowels() {
    if (State.answers.needFinishingTools !== 'yes') return null;
    return { html: 'All finishing tools are shown — we recommend the <strong>No-Burn Chrome Pool Trowel</strong>: the chrome finish won\'t burn (darken) the concrete surface the way steel trowels can.' };
  }

  function recommendStamps() {
    if (State.answers.needStamps !== 'yes') return null;
    const style = State.answers.stampStyle;
    const styleDef = STAMP_STYLES.find(s => s.key === style);
    const lines = [];
    if (styleDef && style !== 'all' && style !== 'tools') {
      lines.push(`Showing <strong>${styleDef.label}</strong> stamps — change the style above to compare other patterns.`);
    } else {
      lines.push('Pick a style above to narrow the patterns, or browse them all.');
    }
    lines.push("Tampers are shown with every style — you'll need one to press the stamp into the concrete.");
    return { html: lines.join('<br>') };
  }

  function recommendPlastiforms() {
    if (State.answers.needPlastiform !== 'yes') return null;
    const ft = num(State.answers.plastiformFt);
    if (ft <= 0) {
      return { html: "Enter how many feet of form board you need above and we'll build the package — boards, connector rods, and camlock clamps." };
    }
    const boards = Math.ceil(ft / COVERAGE.plastiBoardLF);
    const joints = Math.max(0, boards - 1);
    const rods = joints * COVERAGE.plastiRodsPerJoint;
    const clamps = Math.ceil(ft / COVERAGE.plastiClampSpacingFt);
    const lines = [`For <strong>${ft} ft</strong> of form:`];
    lines.push(`• <strong>8' Plastiform Boards:</strong> ${boards} — each board is 8 ft (${boards} × 8 ft = ${boards * 8} ft).`);
    if (rods > 0) lines.push(`• <strong>Connector Rods:</strong> ${rods} — two rods join each pair of boards (${joints} joint${joints > 1 ? 's' : ''}).`);
    lines.push(`• <strong>Camlock Clamps:</strong> ${clamps} — one every 2.5–4 ft (3–4 per board) to clamp the form to the stakes and set its height.`);
    lines.push("The 12' boards are <strong>pickup only</strong> at our PA location — if you can pick up, longer boards mean fewer joints to connect.");
    return { html: lines.join('<br>') };
  }

  // Per-product recommended quantity (returned as integer or null if not applicable)
  function recommendedQtyFor(product, section) {
    if (!product) return null;
    const lf = copingLF();

    if (section === 'forms') {
      if (product.handle === PF.FORM_AUTOCOVER) {
        if (!isAutoCover() || lf <= 0) return null;
        return Math.ceil(lf / acBoxLF(selectedVariantFor(product)));
      }
      if (isAutoCover()) return null;
      const plan = formPlan();
      if (!plan) return null;
      // The qty prefill follows the pre-selected variant (Full when any Fulls
      // are needed, else Half); the badge label shows the full split via
      // RECOMMENDED_LABEL_OVERRIDES.
      if (product.handle === PF.FORM_STRAIGHT) return plan.straightFull || plan.straightHalf || null;
      if (product.handle === PF.FORM_BENDABLE) return plan.bendFull || plan.bendHalf || null;
      return null;
    }

    if (section === 'track') {
      if (isAutoCover() || lf <= 0 || !shapeChosen()) return null;
      const pt = State.answers.poolType;
      const shape = State.answers.poolShape;
      if (pt === 'vinyl') {
        // HZ track is the unbadged alternative: its per-piece qty prefills,
        // but the badge stays on the standard Liner Track (see isRecommended).
        if (product.handle === PF.TRACK_HZ_STRAIGHT || product.handle === PF.TRACK_HZ_NOTCHED) {
          return Math.ceil(lf / COVERAGE.hzPieceLF);
        }
        if (shape === 'rectround') {
          // One bendable case wraps the corners; straight cases only once the
          // footage exceeds the bendable case (track sells by full case).
          if (product.handle === PF.TRACK_LINER_BEND) return 1;
          if (product.handle === PF.TRACK_LINER) {
            if (lf <= 128) return null;
            return Math.ceil((lf - 128) / trackCaseLF(product, selectedVariantFor(product)));
          }
          return null;
        }
        const want = shape === 'freeform' ? PF.TRACK_LINER_BEND : PF.TRACK_LINER;
        if (product.handle !== want) return null;
        return Math.ceil(lf / trackCaseLF(product, selectedVariantFor(product)));
      }
      if (pt === 'fiberglass' || pt === 'gunite') {
        if (shape === 'rectround') {
          if (product.handle === PF.TRACK_RECEIVER_BEND) return 1;
          if (product.handle === PF.TRACK_RECEIVER) {
            if (lf <= 120) return null;
            return Math.ceil((lf - 120) / 120);
          }
          return null;
        }
        const want = shape === 'freeform' ? PF.TRACK_RECEIVER_BEND : PF.TRACK_RECEIVER;
        if (product.handle !== want) return null;
        return Math.ceil(lf / trackCaseLF(product, selectedVariantFor(product)));
      }
      return null;
    }

    if (section === 'liners') {
      if (lf <= 0) return null;
      if (product.handle === PF.LINER_SQUARE_BACKING) return Math.ceil(lf / COVERAGE.squareBackingBoxLF);
      return Math.ceil(lf / COVERAGE.linerPieceLF) + COVERAGE.linerOveragePieces;
    }

    if (section === 'accessories') {
      const noAC = State.answers.autoCover === 'no';
      const pt = State.answers.poolType;
      if (product.handle === PF.ACC_EXTRACTOR) return noAC ? 1 : null;
      if (product.handle === PF.ACC_MOUNT_TAPE) return (noAC && pt === 'fiberglass') ? 1 : null;
      // Screws are recommended (badged) only for concrete/gunite. On fiberglass
      // the mounting tape is the recommended fastener and screws are the shown
      // alternative, so they carry no badge there.
      if (product.handle === PF.ACC_SCREWS) return (noAC && pt === 'gunite') ? 1 : null;
      if (product.handle === PF.ACC_TRIM) return (noAC && (pt === 'fiberglass' || pt === 'gunite')) ? COVERAGE.trimQty : null;
      if (product.handle === PF.ACC_VINYL_TAPE || product.handle === PF.ACC_MALLET) return (noAC && needsBendable()) ? 1 : null;
      return null;
    }

    if (section === 'release') {
      return (product.handle === PF.RELEASE_1GAL && lf > 0 && !hasSquareBackingInCart()) ? 1 : null;
    }

    if (section === 'trowels') {
      if (State.answers.needFinishingTools !== 'yes') return null;
      return product.handle === PF.TROWEL_CHROME ? 1 : null;
    }

    if (section === 'plastiforms') {
      if (State.answers.needPlastiform !== 'yes') return null;
      const ft = num(State.answers.plastiformFt);
      if (ft <= 0) return null;
      const boards = Math.ceil(ft / COVERAGE.plastiBoardLF);
      if (product.handle === PF.PLASTI_BOARD_8) return boards;
      if (product.handle === PF.PLASTI_RODS) {
        const rods = Math.max(0, boards - 1) * COVERAGE.plastiRodsPerJoint;
        return rods > 0 ? rods : null;
      }
      if (product.handle === PF.PLASTI_CLAMPS) return Math.ceil(ft / COVERAGE.plastiClampSpacingFt);
      return null; // 12' boards are pickup-only — shown, never badged
    }

    // Browse sections: skimmer, stamps, skins.
    return null;
  }

  // Unified "should this card show the Recommended badge / sort first" test.
  function isRecommended(product, section) {
    // Liners are a style choice — quantities prefill, but no single liner is
    // badged over another. Same for the HZ alternative tracks.
    if (section === 'liners') return false;
    if (section === 'track' && (product.handle === PF.TRACK_HZ_STRAIGHT || product.handle === PF.TRACK_HZ_NOTCHED)) return false;
    const q = recommendedQtyFor(product, section);
    return q != null && q > 0;
  }

  // Pre-select the right variant per the customer's answers.
  function defaultVariantFor(product) {
    if (!product.variants || !product.variants.length) return null;
    // Coping forms: Full vs Half package per the recommended plan
    if (product.handle === PF.FORM_STRAIGHT || product.handle === PF.FORM_BENDABLE) {
      const plan = formPlan();
      if (plan) {
        const fullCount = product.handle === PF.FORM_STRAIGHT ? plan.straightFull : plan.bendFull;
        const halfCount = product.handle === PF.FORM_STRAIGHT ? plan.straightHalf : plan.bendHalf;
        const want = fullCount > 0 ? /full/i : (halfCount > 0 ? /half/i : null);
        if (want) {
          const v = product.variants.find(v => want.test(v.title || ''));
          if (v) return v;
        }
      }
    }
    // AutoCover form: match the brand answer; prefer the 90" (60 LF) box, but
    // never pre-select a sold-out variant when an in-stock one matches the brand.
    if (product.handle === PF.FORM_AUTOCOVER) {
      const pattern = AC_BRAND_PATTERNS[State.answers.acBrand];
      if (pattern) {
        const matches = product.variants.filter(v => pattern.test(v.title || ''));
        const v90 = matches.find(v => /90/.test(v.title || '') && v.available);
        if (v90) return v90;
        const avail = matches.find(v => v.available);
        if (avail) return avail;
        if (matches.length) return matches[0];
      }
    }
    // Liner Track: prefer the 90" (120 LF) case
    if (product.handle === PF.TRACK_LINER) {
      const v = product.variants.find(v => /90/.test(v.title || '') && v.available);
      if (v) return v;
    }
    // Default: first available variant
    return product.variants.find(v => v.available) || product.variants[0];
  }

  // -------------------- Rendering: product cards --------------------
  // Track previous recommended qty so we can preserve user-customized qty across re-renders.
  const prevRecMap = new Map();
  // Persist user-selected variant choice across re-renders (productId -> variantId).
  const userVariantMap = new Map();
  // Persist multi-variant selections across re-renders (productId -> Set of variantId).
  const multiVariantMap = new Map();

  // Compute the default multi-variant selection for a product (by matching
  // variant titles against the configured defaults), used on first render.
  function defaultMultiSelection(product) {
    const patterns = MULTI_VARIANT_DEFAULTS[product.handle];
    const set = new Set();
    if (!patterns) return set;
    product.variants.forEach(v => {
      if (patterns.indexOf(v.title) !== -1) set.add(v.id);
    });
    return set;
  }
  function getMultiSelection(product) {
    if (multiVariantMap.has(product.id)) return multiVariantMap.get(product.id);
    const def = defaultMultiSelection(product);
    multiVariantMap.set(product.id, def);
    return def;
  }

  function renderProductCard(product, section) {
    if (!product) return '';
    const recQty = recommendedQtyFor(product, section);
    // Badge / sort-first status. Usually this is just "has a recommended qty",
    // but sealers can be recommended (by location + finish) before a sq ft
    // value makes a quantity available.
    const recommended = isRecommended(product, section);
    const defaultVariant = defaultVariantFor(product);
    const hasVariants = !product.hasOnlyDefaultVariant && product.variants.length > 1;
    const isMultiVariant = hasVariants && (product.tags || []).indexOf('wizard:multi-variant') !== -1;
    const isBracket = section === 'brackets' && hasVariants && product.optionsWithValues && product.optionsWithValues.length > 1;
    const isColoring = section === 'coloring' && hasVariants && !isMultiVariant && !isBracket;

    // Decide which variant is selected: user choice if they made one, else default
    let selectedVariant = defaultVariant;
    const userChoiceId = userVariantMap.get(product.id);
    if (userChoiceId) {
      const found = product.variants.find(v => v.id === userChoiceId);
      if (found) selectedVariant = found;
    }

    // Preserve user-customized qty across re-renders.
    // Only treat the existing qty as "user-customized" if the previous render
    // had a meaningful (> 0) recommendation. Otherwise the existing qty is just
    // the fallback default of 1 and we should let the new recommendation win.
    const existingCard = root.querySelector(`.ctw-product[data-product-id="${product.id}"]`);
    const existingQty = existingCard ? num(existingCard.querySelector('.ctw-product__qty')?.value) : 0;
    const prevRec = prevRecMap.get(product.id);
    let initialQty;
    if (existingQty > 0 && prevRec != null && prevRec > 0 && existingQty !== prevRec) {
      // User customized — keep their value
      initialQty = existingQty;
    } else {
      initialQty = (recQty != null && recQty > 0) ? recQty : 1;
    }
    prevRecMap.set(product.id, recQty);

    const outOfStock = !product.available;
    // The product may be available overall but the SELECTED variant sold out
    // (a sold-out option chosen from the dropdown). Block adding that variant.
    const selUnavailable = !isMultiVariant && !isBracket && selectedVariant && !selectedVariant.available;

    // ---------- Variant UI ----------
    let variantHtml = '';
    if (isMultiVariant) {
      const selectedIds = getMultiSelection(product);
      const items = product.variants.map(v => {
        const isSel = selectedIds.has(v.id);
        const oos = !v.available;
        return `<label class="ctw-multi-variant${oos ? ' is-sold-out' : ''}">
          <input type="checkbox" class="ctw-multi-variant__cb" data-variant-id="${v.id}" data-product-id="${product.id}"${isSel ? ' checked' : ''}${oos ? ' disabled' : ''}>
          <span class="ctw-multi-variant__title">${escapeHtml(v.title)}${oos ? ' (Sold out)' : ''}</span>
          <span class="ctw-multi-variant__price">${escapeHtml(v.priceFormatted)}</span>
        </label>`;
      }).join('');
      variantHtml = `<div class="ctw-product__variant ctw-product__variant--multi">
        <div class="ctw-multi-variant__heading">Select grits</div>
        <div class="ctw-multi-variants">${items}</div>
      </div>`;
    } else if (hasVariants) {
      const opts = product.variants.map(v => {
        const oosLabel = !v.available ? ' (Sold out)' : '';
        const isSel = selectedVariant && v.id === selectedVariant.id ? ' selected' : '';
        return `<option value="${v.id}"${isSel}>${escapeHtml(v.title)} — ${escapeHtml(v.priceFormatted)}${oosLabel}</option>`;
      }).join('');

      if (isBracket) {
        // Per-option dropdowns: Thickness, Length, (Height)
        const sortedOpts = product.optionsWithValues.slice().sort((a, b) => a.position - b.position);
        const optHtml = sortedOpts.map(opt => {
          const items = opt.values.map(val =>
            `<option value="${escapeHtml(val)}">${escapeHtml(val)}</option>`
          ).join('');
          return `<div class="ctw-bracket-opt">
            <label>${escapeHtml(opt.name)}</label>
            <select class="ctw-bracket-select" data-option-position="${opt.position}" data-product-id="${product.id}">
              <option value="">— Select —</option>
              ${items}
            </select>
          </div>`;
        }).join('');
        variantHtml = `<div class="ctw-product__variant ctw-product__variant--bracket">
          ${optHtml}
          <div class="ctw-bracket-status" data-bracket-status="${product.id}"></div>
          <select class="ctw-product__variant-select" data-product-id="${product.id}" hidden aria-hidden="true">${opts}</select>
        </div>`;
      } else if (isColoring) {
        // Color swatches: small clickable thumbnails that drive a hidden select
        const swatches = product.variants.map(v => {
          const isSel = selectedVariant && v.id === selectedVariant.id;
          const oos = !v.available;
          const img = v.image ? `<img src="${escapeHtml(v.image)}" alt="${escapeHtml(v.title)}">` : '';
          return `<button type="button" class="ctw-swatch${isSel ? ' is-selected' : ''}${oos ? ' is-sold-out' : ''}" data-variant-id="${v.id}" title="${escapeHtml(v.title)}${oos ? ' (Sold out)' : ''}" aria-label="${escapeHtml(v.title)}">${img}</button>`;
        }).join('');
        variantHtml = `<div class="ctw-product__variant ctw-product__variant--swatches">
          <div class="ctw-swatches">${swatches}</div>
          <div class="ctw-swatch__selected-name">Selected: <strong>${escapeHtml(selectedVariant.title)}</strong></div>
          <select class="ctw-product__variant-select" data-product-id="${product.id}" hidden aria-hidden="true">${opts}</select>
        </div>`;
      } else {
        // Standard dropdown
        variantHtml = `<div class="ctw-product__variant">
          <label for="ctw-variant-${product.id}">Variant</label>
          <select id="ctw-variant-${product.id}" class="ctw-product__variant-select" data-product-id="${product.id}">${opts}</select>
        </div>`;
      }
    }

    const recLabelRaw = RECOMMENDED_LABEL_OVERRIDES[product.handle];
    const recLabel = typeof recLabelRaw === 'function' ? recLabelRaw(product, section) : recLabelRaw;
    // Show "Recommended: N" when we know the quantity, else a plain
    // "Recommended" line for items recommended before sq ft is entered.
    const recBadge = recommended
      ? `<div class="ctw-product__rec">${recLabel ? escapeHtml(recLabel) : (recQty != null && recQty > 0 ? 'Recommended: ' + recQty : 'Recommended')}</div>` : '';

    // One-click "add both packages" block, only on a coping-form card whose
    // recommendation is a Full + Half combination. The breakdown text comes from
    // the SAME packPartsText source as the badge, so they can't drift. The manual
    // dropdown + qty stay below for a custom single add.
    const combo = section === 'forms' ? formComboPlan(product) : null;
    const comboHtml = combo
      ? `<div class="ctw-product__combo">
          <div class="ctw-product__combo-note">This recommendation needs two package sizes:</div>
          <button type="button" class="ctw-product__add-combo" data-add-combo="${product.id}">Add recommended package: ${escapeHtml(combo.text)}</button>
          <div class="ctw-product__combo-or">or choose a size below</div>
        </div>`
      : '';

    const maxQty = (selectedVariant && selectedVariant.inventoryManagement === 'shopify' && selectedVariant.inventoryPolicy !== 'continue')
      ? Math.max(1, selectedVariant.inventoryQuantity) : '';

    const imageUrl = (selectedVariant && selectedVariant.image) || product.image || '';
    const imageHoverUrl = product.image2 || '';
    // Color products: don't show secondary-image-on-hover (variant swatch IS the primary image)
    const imageHtml = imageUrl
      ? `<img class="ctw-product__img ctw-product__img--primary" src="${escapeHtml(imageUrl)}" alt="${escapeHtml(product.title)}" loading="lazy">` +
        (!isColoring && imageHoverUrl ? `<img class="ctw-product__img ctw-product__img--hover" src="${escapeHtml(imageHoverUrl)}" alt="" loading="lazy" aria-hidden="true">` : '')
      : '';

    return `
      <article class="ctw-product${recommended ? ' is-recommended' : ''}${combo ? ' is-combo' : ''}" data-product-id="${product.id}">
        ${recommended ? '<span class="ctw-product__badge">Recommended</span>' : ''}
        <div class="ctw-product__image-wrap">
          <a href="${escapeHtml(product.url)}" data-ctw-quickview-open="${escapeHtml(product.handle)}" aria-label="Quick view: ${escapeHtml(product.title)}">${imageHtml}</a>
        </div>
        <h3 class="ctw-product__title"><a href="${escapeHtml(product.url)}" data-ctw-quickview-open="${escapeHtml(product.handle)}">${escapeHtml(product.title)}</a></h3>
        <div class="ctw-product__price">${escapeHtml((selectedVariant && selectedVariant.priceFormatted) || product.priceFormatted || money(product.price))}</div>
        ${recBadge}
        ${comboHtml}
        ${variantHtml}
        <div class="ctw-product__qty-row">
          <label for="ctw-qty-${product.id}">Qty</label>
          <input type="number" id="ctw-qty-${product.id}" class="ctw-product__qty" value="${initialQty}" min="1"${maxQty ? ` max="${maxQty}"` : ''}>
        </div>
        ${outOfStock ? '<div class="ctw-product__stockmsg is-error">Out of stock</div>' : ''}
        <button type="button" class="ctw-product__add" data-add-product="${product.id}"${isMultiVariant ? ' data-multi-variant="1"' : ''}${(outOfStock || isBracket || selUnavailable) ? ' disabled' : ''}>
          ${outOfStock ? 'Out of stock' : (isBracket ? 'Select options first' : (selUnavailable ? 'Sold out' : (isMultiVariant ? 'Add Selected to Cart' : 'Add to Cart')))}
        </button>
      </article>`;
  }

  // Vinyl-liner-only compatibility callout shown below the track recommendation.
  // Pre-installed receiver track on a vinyl liner pool may already work with the
  // Z Poolform liner — if it's one of these, the customer can skip the section.
  function trackCompatibilityAlert() {
    if (State.answers.poolType !== 'vinyl' || isAutoCover()) return null;
    return '<div class="ctw-alert__title">Already have receiver track for your vinyl liner pool?</div>' +
      'If your vinyl liner pool came with a receiver track included, make sure it is compatible with the Z Poolform liner before ordering — if it is listed below, you can <strong>skip this section</strong>. Known compatible liner tracks:' +
      '<ul>' +
        '<li><strong>Cardinal</strong> — HZ liner track</li>' +
        '<li><strong>Cinderella</strong> — HM-1 liner track</li>' +
        '<li><strong>Latham</strong> — COP-801-9535 liner track</li>' +
        '<li><strong>Only Alpha</strong> — 1106 Flat Track</li>' +
        '<li><strong>Fox Pools</strong> — liner track</li>' +
      '</ul>';
  }

  function renderSection(section) {
    const container = root.querySelector(`[data-products="${section}"]`);
    if (!container) return;
    const list = productsForSection(section);
    // Sort: products with a positive recommended qty come first; otherwise
    // preserve the original (collection / popularity) order. Stable in modern browsers.
    const sorted = list.slice().sort((a, b) => {
      const aHas = isRecommended(a, section) ? 1 : 0;
      const bHas = isRecommended(b, section) ? 1 : 0;
      return bHas - aHas;
    });
    if (!sorted.length) {
      container.innerHTML = '<p style="opacity:0.6;font-size:14px">No products available for your current selections.</p>';
    } else {
      container.innerHTML = sorted.map(p => renderProductCard(p, section)).join('');
    }
    // Recommendation banner
    const recEl = root.querySelector(`[data-recommendation="${section}"]`);
    if (recEl) {
      const rec = section === 'forms' ? recommendForms()
                : section === 'track' ? recommendTrack()
                : section === 'liners' ? recommendLiners()
                : section === 'accessories' ? recommendAccessories()
                : section === 'release' ? recommendRelease()
                : section === 'trowels' ? recommendTrowels()
                : section === 'stamps' ? recommendStamps()
                : section === 'plastiforms' ? recommendPlastiforms()
                : null;
      if (rec && rec.html) {
        recEl.innerHTML = '<div class="ctw-recommendation__title">Recommended based on provided details</div>' + rec.html;
        recEl.hidden = false;
      } else {
        recEl.hidden = true;
        recEl.innerHTML = '';
      }
    }

    // Per-section alert callout below the recommendation (track-only for now):
    // the vinyl-liner receiver-track compatibility note.
    const alertEl = root.querySelector(`[data-alert="${section}"]`);
    if (alertEl) {
      const alertHtml = section === 'track' ? trackCompatibilityAlert() : null;
      if (alertHtml) { alertEl.innerHTML = alertHtml; alertEl.hidden = false; }
      else { alertEl.hidden = true; alertEl.innerHTML = ''; }
    }
  }

  function renderAllSections() { SECTIONS.forEach(renderSection); }

  // -------------------- Cart panel rendering --------------------
  function renderCartPanel() {
    const items = State.cart.items || [];
    const countEl = $('#ctw-cart-count');
    const subEl = $('#ctw-cart-subtotal');
    const sub2El = $('#ctw-cart-subtotal-2');
    const itemsEl = $('#ctw-cart-items');
    const emptyEl = $('#ctw-cart-empty');

    const count = items.reduce((acc, i) => acc + i.quantity, 0);
    countEl.textContent = `${count} ${count === 1 ? 'item' : 'items'}`;
    subEl.textContent = money(State.cart.total_price || 0);
    if (sub2El) sub2El.textContent = money(State.cart.total_price || 0);

    if (!items.length) {
      emptyEl.classList.remove('is-hidden');
      itemsEl.innerHTML = '';
      return;
    }
    emptyEl.classList.add('is-hidden');
    itemsEl.innerHTML = items.map((item, idx) => `
      <li class="ctw__cart-item">
        ${item.image ? `<img class="ctw__cart-item-img" src="${escapeHtml(item.image)}" alt="">` : '<div class="ctw__cart-item-img"></div>'}
        <div class="ctw__cart-item-info">
          <div class="ctw__cart-item-title">${escapeHtml(item.product_title)}</div>
          ${item.variant_title && item.variant_title !== 'Default Title' ? `<div class="ctw__cart-item-variant">${escapeHtml(item.variant_title)}</div>` : ''}
          <div class="ctw__cart-item-meta">
            <span>Qty ${item.quantity} · ${money(item.final_line_price)}</span>
            <button type="button" class="ctw__cart-item-remove" data-remove-variant="${item.id}">Remove</button>
          </div>
        </div>
      </li>`).join('');
  }

  // -------------------- Event handlers --------------------
  function bindQuestionInputs() {
    // sqFt syncs across all sections that ask for it. Same for any other shared keys.
    $$('input[data-answer], select[data-answer]').forEach(el => {
      const key = el.dataset.answer;
      // Set initial value from state
      if (State.answers[key] !== undefined && State.answers[key] !== '' && State.answers[key] !== 0) {
        el.value = State.answers[key];
      }
      el.addEventListener('input', debounce(() => {
        const val = el.type === 'number' ? num(el.value) : el.value;
        State.answers[key] = val;
        // Sync all inputs with the same data-answer key
        $$(`[data-answer="${key}"]`).forEach(other => {
          if (other !== el) other.value = el.value;
        });
        saveState();
        applyConditionalFields();
        renderAllSections();
      }, 200));
    });
    applyConditionalFields();
  }

  function applyConditionalFields() {
    $$('[data-show-if]').forEach(el => {
      const cond = el.dataset.showIf; // e.g., "sink=yes"
      const [key, val] = cond.split('=');
      el.classList.toggle('is-visible', String(State.answers[key]) === val);
    });

    // Auto-cover pools are rectangular — drop "Freeform" from the pool-shape
    // question while Auto Cover = Yes (disabled + hidden), and clear the answer
    // if Freeform was already selected so it can't carry through.
    const shapeSel = $('#ctw-pool-shape');
    if (shapeSel) {
      const hideFreeform = State.answers.autoCover === 'yes';
      const ff = shapeSel.querySelector('option[value="freeform"]');
      if (ff) { ff.hidden = hideFreeform; ff.disabled = hideFreeform; }
      if (hideFreeform && State.answers.poolShape === 'freeform') {
        State.answers.poolShape = '';
        shapeSel.value = '';
        saveState();
      }
    }
  }

  // Cross-product compatibility warnings, shared by the card Add buttons and the
  // quick-view Add. Returns false to abort the add (after a declined confirm()).
  // No Poolform incompatibility rules are defined yet — when they are, follow the
  // countertop wizard pattern: confirm() guards that fire in both directions
  // (adding A with B in cart, and B with A in cart).
  function runAddCompatibilityChecks(product) {
    return true;
  }

  function bindProductActions() {
    root.addEventListener('click', async (e) => {
      // One-click "add recommended package" (Full + Half of one form, in a
      // single deferred add). Recompute the combo fresh — never trust stale DOM.
      const comboBtn = e.target.closest('[data-add-combo]');
      if (comboBtn) {
        const product = PRODUCTS.find(p => p.id === parseInt(comboBtn.dataset.addCombo, 10));
        if (!product) return;
        const combo = formComboPlan(product);
        if (!combo) return;
        if (!runAddCompatibilityChecks(product)) return;
        // Best-effort flash (the section re-render in enqueueAdd replaces this
        // card; the durable confirmation is the cart pill jumping + both lines
        // appearing in the panel).
        comboBtn.classList.add('is-added');
        comboBtn.textContent = '✓ Added';
        enqueueAdd([
          { id: combo.fullVariant.id, quantity: combo.fullCount },
          { id: combo.halfVariant.id, quantity: combo.halfCount },
        ]);
        return;
      }

      // Variant change updates the qty max and price display
      // Add to cart
      const addBtn = e.target.closest('[data-add-product]');
      if (addBtn) {
        const productId = parseInt(addBtn.dataset.addProduct, 10);
        const product = PRODUCTS.find(p => p.id === productId);
        if (!product) return;
        const card = addBtn.closest('.ctw-product');
        const qtyInput = card.querySelector('.ctw-product__qty');
        const qty = Math.max(1, num(qtyInput.value));
        const isMulti = addBtn.dataset.multiVariant === '1';

        let variantIdsToAdd = [];
        if (isMulti) {
          variantIdsToAdd = Array.from(card.querySelectorAll('.ctw-multi-variant__cb:checked'))
            .map(cb => parseInt(cb.dataset.variantId, 10));
          if (!variantIdsToAdd.length) {
            alert('Please check at least one option to add.');
            return;
          }
        } else {
          const variantSel = card.querySelector('.ctw-product__variant-select');
          const vid = variantSel ? parseInt(variantSel.value, 10) : (product.variants[0] && product.variants[0].id);
          // Never add a sold-out variant (the button should already be disabled,
          // but guard in case the selection slipped through).
          const chosen = product.variants.find(v => v.id === vid);
          if (chosen && !chosen.available) return;
          variantIdsToAdd = [vid];
        }

        // Cross-product compatibility warnings (shared with the quick-view add).
        if (!runAddCompatibilityChecks(product)) return;

        addBtn.disabled = true;
        const originalLabel = addBtn.textContent;
        // Instant, purely local add — the real /cart/add.js write happens once,
        // at a sync point (cart/checkout click, drawer open, or page leave).
        // See the deferred add queue. Zero cart-API traffic while picking
        // products = the Cloudflare rate-limit lockout can't trigger here.
        const items = variantIdsToAdd.map(vid => ({ id: vid, quantity: qty }));
        enqueueAdd(items);
        addBtn.classList.add('is-added');
        addBtn.textContent = variantIdsToAdd.length > 1 ? `✓ Added ${variantIdsToAdd.length}` : '✓ Added';
        setTimeout(() => {
          addBtn.classList.remove('is-added');
          addBtn.disabled = false;
          addBtn.textContent = originalLabel;
        }, 1500);
        return;
      }

      // Remove an item (by variant). The local pending portion is dropped with
      // zero network; only a portion that already lives in the SERVER cart
      // needs a single /cart/change.js call.
      const rmBtn = e.target.closest('[data-remove-variant]');
      if (rmBtn) {
        const vid = parseInt(rmBtn.dataset.removeVariant, 10);
        rmBtn.disabled = true;
        try {
          const qIdx = _addQueue.findIndex(q => q.id === vid);
          if (qIdx !== -1) {
            _addQueue.splice(qIdx, 1);
            persistPendingAdds();
          }
          const serverItems = (State.serverCart && State.serverCart.items) || [];
          const lineIdx = serverItems.findIndex(i => i.id === vid);
          if (lineIdx !== -1) {
            await changeLine(lineIdx + 1, 0); // refetches + recomputes merged view
            refreshThemeCartNow(); // keep the site cart drawer in sync
          } else {
            recomputeMergedCart();
          }
          renderCartPanel();
          renderAllSections();
        } catch (err) {
          console.error('[CTW] remove error', err);
          rmBtn.disabled = false; // let the customer retry (or see the banner)
          handleCartError(err);
        }
        return;
      }
    });

    // Variant change: update product image / price within the card
    root.addEventListener('change', (e) => {
      const sel = e.target.closest('.ctw-product__variant-select');
      if (!sel) return;
      const card = sel.closest('.ctw-product');
      const productId = parseInt(sel.dataset.productId, 10);
      const product = PRODUCTS.find(p => p.id === productId);
      const variant = product && product.variants.find(v => v.id === parseInt(sel.value, 10));
      if (!variant) return;
      // Remember the user's choice so re-renders preserve it
      userVariantMap.set(productId, variant.id);
      const priceEl = card.querySelector('.ctw-product__price');
      if (priceEl) priceEl.textContent = variant.priceFormatted;
      const imgEl = card.querySelector('.ctw-product__img--primary');
      if (imgEl && variant.image) imgEl.src = variant.image;
      const qtyEl = card.querySelector('.ctw-product__qty');
      if (qtyEl && variant.inventoryManagement === 'shopify' && variant.inventoryPolicy !== 'continue') {
        qtyEl.max = Math.max(1, variant.inventoryQuantity);
      } else if (qtyEl) {
        qtyEl.removeAttribute('max');
      }
      // Update "Selected: …" label for swatch variants
      const nameEl = card.querySelector('.ctw-swatch__selected-name strong');
      if (nameEl) nameEl.textContent = variant.title;
      // Block add-to-cart when a sold-out variant is chosen from the dropdown.
      // Only for the visible standard dropdown — bracket/swatch hidden selects
      // manage their own Add button state.
      if (!sel.hidden) {
        const addBtn = card.querySelector('.ctw-product__add');
        if (addBtn && !addBtn.hasAttribute('data-multi-variant')) {
          addBtn.disabled = !variant.available;
          addBtn.textContent = variant.available ? 'Add to Cart' : 'Sold out';
        }
      }
    });

    // Bracket per-option dropdown changes → find matching variant
    root.addEventListener('change', (e) => {
      const sel = e.target.closest('.ctw-bracket-select');
      if (!sel) return;
      const card = sel.closest('.ctw-product');
      if (!card) return;
      const productId = parseInt(card.dataset.productId, 10);
      const product = PRODUCTS.find(p => p.id === productId);
      if (!product) return;
      const optSelects = card.querySelectorAll('.ctw-bracket-select');
      const chosen = {};
      let allChosen = true;
      optSelects.forEach(s => {
        const pos = parseInt(s.dataset.optionPosition, 10);
        if (!s.value) allChosen = false;
        chosen[pos] = s.value;
      });
      const hiddenSel = card.querySelector('.ctw-product__variant-select');
      const addBtn = card.querySelector('.ctw-product__add');
      const statusEl = card.querySelector('.ctw-bracket-status');

      if (!allChosen) {
        if (addBtn) { addBtn.disabled = true; addBtn.textContent = 'Select options first'; }
        if (statusEl) statusEl.textContent = '';
        return;
      }
      // Match the variant against chosen option1 / option2 / option3
      const matching = product.variants.find(v =>
        (v.option1 || '') === (chosen[1] || '') &&
        (v.option2 || '') === (chosen[2] || '') &&
        (v.option3 || '') === (chosen[3] || '')
      );
      if (matching && matching.available) {
        if (hiddenSel) {
          hiddenSel.value = matching.id;
          hiddenSel.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (addBtn) { addBtn.disabled = false; addBtn.textContent = 'Add to Cart'; }
        if (statusEl) { statusEl.textContent = ''; statusEl.classList.remove('is-error'); }
      } else if (matching && !matching.available) {
        if (addBtn) { addBtn.disabled = true; addBtn.textContent = 'Sold out'; }
        if (statusEl) { statusEl.textContent = 'This combination is sold out.'; statusEl.classList.add('is-error'); }
      } else {
        if (addBtn) { addBtn.disabled = true; addBtn.textContent = 'Add to Cart'; }
        if (statusEl) { statusEl.textContent = 'This combination is not available.'; statusEl.classList.add('is-error'); }
      }
    });

    // Multi-variant checkbox changes → persist selection across re-renders
    root.addEventListener('change', (e) => {
      const cb = e.target.closest('.ctw-multi-variant__cb');
      if (!cb) return;
      const productId = parseInt(cb.dataset.productId, 10);
      const variantId = parseInt(cb.dataset.variantId, 10);
      let set = multiVariantMap.get(productId);
      if (!set) { set = new Set(); multiVariantMap.set(productId, set); }
      if (cb.checked) set.add(variantId);
      else set.delete(variantId);
    });

    // Color swatch clicks → sync hidden select + dispatch change
    root.addEventListener('click', (e) => {
      const sw = e.target.closest('.ctw-swatch');
      if (!sw) return;
      e.preventDefault();
      const card = sw.closest('.ctw-product');
      const hiddenSel = card.querySelector('.ctw-product__variant-select');
      if (!hiddenSel) return;
      const variantId = sw.dataset.variantId;
      hiddenSel.value = variantId;
      // Update selected class on swatches
      card.querySelectorAll('.ctw-swatch').forEach(s => s.classList.toggle('is-selected', s === sw));
      hiddenSel.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  function bindCartToggle() {
    const cartEl = $('#ctw-cart-panel');
    const toggle = $('#ctw-cart-toggle');
    toggle.addEventListener('click', () => {
      const open = cartEl.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  // iOS leaves visualViewport.offsetTop non-zero after the keyboard closes, which
  // offsets the TAP target of a fixed element from where it's drawn — so taps on
  // the floating cart pill miss. We nudge the pill's real `bottom` so its hit
  // area realigns with its visual position (a small gap above the visible
  // viewport bottom). Settle-debounced (never act on transient mid-scroll
  // geometry); clamps non-negative; NEVER touches the viewport <meta> (that broke
  // the theme header). Mobile only — desktop is the sidebar.
  function bindFloatingCartTouchFix() {
    const cart = $('#ctw-cart-panel');
    const vv = window.visualViewport;
    if (!cart || !vv) return;
    const GAP = 18; // px above the visible viewport bottom (matches the CSS float)
    let t = null, keyboardOpen = false;
    function correct() {
      t = null;
      // Desktop sidebar, keyboard up, or pinch-zoom → defer to the CSS base.
      if (window.innerWidth > 900 || keyboardOpen || Math.abs(vv.scale - 1) > 0.01) {
        cart.style.bottom = '';
        return;
      }
      const cur = parseFloat(cart.style.bottom) || 0;
      const rectBottom = cart.getBoundingClientRect().bottom;
      const target = (vv.offsetTop + vv.height) - GAP;
      const next = Math.max(0, Math.round(cur + (rectBottom - target)));
      if (Math.abs(next - cur) > 1) cart.style.bottom = next + 'px';
    }
    function schedule() { clearTimeout(t); t = setTimeout(correct, 140); }
    vv.addEventListener('resize', schedule);
    vv.addEventListener('scroll', schedule);
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('orientationchange', schedule);
    window.addEventListener('resize', schedule);
    document.addEventListener('focusin', (e) => {
      if (e.target && e.target.matches && e.target.matches('input, select, textarea')) {
        keyboardOpen = true; cart.style.bottom = '';
      }
    });
    document.addEventListener('focusout', () => { keyboardOpen = false; [150, 400, 800].forEach(d => setTimeout(schedule, d)); });
    setTimeout(correct, 120);
  }

  function bindReset() {
    $('#ctw-reset').addEventListener('click', async () => {
      if (!confirm('Clear all your answers and remove everything from your cart?')) return;
      try {
        // Drop the local pending picks and let any in-flight flush land FIRST,
        // so a late flush can't re-populate the cart immediately after clearing.
        cancelPendingAdds();
        if (_drainPromise) { try { await _drainPromise; } catch (e) { /* ignore */ } }
        // Only hit the server if it actually holds items — a purely local
        // session resets with ZERO cart-API calls.
        if (((State.serverCart && State.serverCart.items) || []).length) {
          await clearCart();
          refreshThemeCartNow(); // empty the site cart drawer right away
        } else {
          recomputeMergedCart();
        }
        clearState();
        userVariantMap.clear();
        prevRecMap.clear();
        multiVariantMap.clear();
        // Reset all input values
        $$('input[data-answer]').forEach(el => { el.value = ''; });
        $$('select[data-answer]').forEach(el => { el.value = ''; });
        applyConditionalFields();
        renderAllSections();
        renderCartPanel();
      } catch (e) {
        console.error('[CTW] reset error', e);
        handleCartError(e); // if the cart API is locked, point at the refresh banner
      }
    });
  }

  function bindTooltipsMobile() {
    // Tooltip implementation: a single absolutely-positioned popup appended to
    // <body> so it escapes ancestor overflow/clipping and always fits the
    // viewport. Shown on hover/focus (desktop) and tap (mobile).
    let popup = null;
    function hide() {
      if (popup) { popup.remove(); popup = null; }
    }
    function show(trigger) {
      const text = trigger.getAttribute('data-ctw-tip') || trigger.getAttribute('data-tooltip');
      if (!text) return;
      hide();
      popup = document.createElement('div');
      popup.className = 'ctw-tooltip-popup';
      popup.textContent = text;
      document.body.appendChild(popup);
      // Position above trigger, clamped to viewport
      const r = trigger.getBoundingClientRect();
      const pr = popup.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollX = window.scrollX || window.pageXOffset;
      let top = r.top + scrollY - pr.height - 10;
      // If no room above, place below
      if (top < scrollY + 8) top = r.bottom + scrollY + 10;
      let left = r.left + scrollX + (r.width / 2) - (pr.width / 2);
      const minLeft = scrollX + 8;
      const maxLeft = scrollX + window.innerWidth - pr.width - 8;
      if (left < minLeft) left = minLeft;
      if (left > maxLeft) left = maxLeft;
      popup.style.top = top + 'px';
      popup.style.left = left + 'px';
      // Position the little arrow under the trigger's center even if the
      // popup got clamped sideways.
      const arrowLeft = (r.left + scrollX + r.width / 2) - left;
      popup.style.setProperty('--arrow-left', arrowLeft + 'px');
    }

    // Desktop: hover / focus
    root.addEventListener('mouseover', (e) => {
      const t = e.target.closest('.ctw-tooltip');
      if (t) show(t);
    });
    root.addEventListener('mouseout', (e) => {
      const t = e.target.closest('.ctw-tooltip');
      if (t) hide();
    });
    root.addEventListener('focusin', (e) => {
      const t = e.target.closest('.ctw-tooltip');
      if (t) show(t);
    });
    root.addEventListener('focusout', (e) => {
      const t = e.target.closest('.ctw-tooltip');
      if (t) hide();
    });
    // Mobile / touch: tap toggles
    root.addEventListener('click', (e) => {
      const t = e.target.closest('.ctw-tooltip');
      if (!t) { hide(); return; }
      e.preventDefault();
      if (popup) hide();
      else show(t);
    });
    // Hide on scroll/resize so it doesn't drift
    window.addEventListener('scroll', hide, { passive: true });
    window.addEventListener('resize', hide);
  }

  // Variants visible for a product given current answers. No Poolform variant-
  // hiding rules yet, so this returns every variant; kept as a seam (the
  // countertop wizard hides one Color-Pack swatch outdoors here).
  function visibleVariantsFor(product) {
    return product.variants || [];
  }

  // -------------------- Slide-up Quick View modal --------------------
  // Fetches /products/<handle>?view=ctw (a lean layout:false template) and injects
  // the rendered main-product section so the customer can read full product details
  // without leaving the wizard.
  // Build the quick-view add-to-cart bar — title, SKU, Yotpo star rating, price,
  // (variant picker if needed), a qty prefilled from the wizard's recommendation,
  // and an Add button that routes through the SAME deferred local-first cart as
  // every other Add button (no immediate /cart write).
  function buildQuickViewAdd(product, section, onAdded) {
    if (!product || !product.variants || !product.variants.length) return null;
    const variants = visibleVariantsFor(product);
    if (!variants.length) return null;
    let sel = defaultVariantFor(product);
    if (!sel || !variants.some(v => v.id === sel.id)) sel = variants[0];
    const recQty = recommendedQtyFor(product, section);
    const initQty = (recQty != null && recQty > 0) ? recQty : 1;
    const multiVariant = variants.length > 1;
    // Full + Half combo (forms only) — a one-click "add both" button, mirroring
    // the product card. The manual dropdown + qty below still do a custom add.
    const combo = section === 'forms' ? formComboPlan(product) : null;

    const wrap = document.createElement('div');
    wrap.className = 'ctw-qv-add' + (combo ? ' is-combo' : '');
    wrap.innerHTML =
      '<div class="ctw-qv-add__info">' +
        '<div class="ctw-qv-add__title">' + escapeHtml(product.title) + '</div>' +
        '<div class="ctw-qv-add__sku" data-qv-sku>' + (sel.sku ? 'SKU: ' + escapeHtml(sel.sku) : '') + '</div>' +
        '<div class="yotpo bottomLine" data-product-id="' + product.id + '"></div>' +
        '<div class="ctw-qv-add__price" data-qv-price>' + escapeHtml(sel.priceFormatted || money(sel.price)) + '</div>' +
      '</div>' +
      (combo ? '<button type="button" class="ctw-qv-add__combo" data-qv-combo>Add recommended package: ' + escapeHtml(combo.text) + '</button>' : '') +
      '<div class="ctw-qv-add__controls">' +
        (multiVariant
          ? '<select class="ctw-qv-add__variant" aria-label="Option">' +
              variants.map(v => '<option value="' + v.id + '"' + (v.id === sel.id ? ' selected' : '') + '>' +
                escapeHtml(v.title) + (v.available ? '' : ' (Sold out)') + '</option>').join('') +
            '</select>'
          : '') +
        '<div class="ctw-qv-add__qtyrow"><label>Qty</label>' +
          '<input type="number" class="ctw-qv-add__qty" min="1" value="' + initQty + '"></div>' +
        '<button type="button" class="ctw-qv-add__btn"' + (sel.available ? '' : ' disabled') + '>' +
          (sel.available ? 'Add to Cart' : 'Out of stock') + '</button>' +
      '</div>';

    const variantSel = wrap.querySelector('.ctw-qv-add__variant');
    const skuEl = wrap.querySelector('[data-qv-sku]');
    const priceEl = wrap.querySelector('[data-qv-price]');
    const qtyEl = wrap.querySelector('.ctw-qv-add__qty');
    const btn = wrap.querySelector('.ctw-qv-add__btn');
    const currentVariant = () => {
      const id = variantSel ? parseInt(variantSel.value, 10) : sel.id;
      return variants.find(v => v.id === id) || sel;
    };
    if (variantSel) {
      variantSel.addEventListener('change', () => {
        const v = currentVariant();
        if (skuEl) skuEl.textContent = v.sku ? 'SKU: ' + v.sku : '';
        if (priceEl) priceEl.textContent = v.priceFormatted || money(v.price);
        btn.disabled = !v.available;
        btn.textContent = v.available ? 'Add to Cart' : 'Out of stock';
      });
    }
    btn.addEventListener('click', () => {
      const v = currentVariant();
      if (!v.available) return;
      if (!runAddCompatibilityChecks(product)) return;
      const qty = Math.max(1, num(qtyEl.value));
      enqueueAdd([{ id: v.id, quantity: qty }]);
      btn.disabled = true;
      btn.classList.add('is-added');
      btn.textContent = '✓ Added';
      // Close the quick view shortly after, revealing the updated mini-cart.
      setTimeout(() => { if (typeof onAdded === 'function') onAdded(); }, 650);
    });
    const comboBtn = wrap.querySelector('[data-qv-combo]');
    if (comboBtn && combo) {
      comboBtn.addEventListener('click', () => {
        if (!runAddCompatibilityChecks(product)) return;
        enqueueAdd([
          { id: combo.fullVariant.id, quantity: combo.fullCount },
          { id: combo.halfVariant.id, quantity: combo.halfCount },
        ]);
        comboBtn.disabled = true;
        comboBtn.classList.add('is-added');
        comboBtn.textContent = '✓ Added';
        setTimeout(() => { if (typeof onAdded === 'function') onAdded(); }, 650);
      });
    }
    return wrap;
  }

  function refreshYotpo() {
    try {
      if (window.yotpo && typeof window.yotpo.refreshWidgets === 'function') window.yotpo.refreshWidgets();
      else if (window.yotpoWidgetsContainer && window.yotpoWidgetsContainer.initWidgets) window.yotpoWidgetsContainer.initWidgets();
    } catch (e) { /* widget lib not ready — non-fatal */ }
  }

  // The full Yotpo reviews widget for the bottom of the quick view — the same
  // legacy main widget the product page renders under the description. Yotpo's
  // loader (already on the page; it powers the star ratings) fills it in on
  // refreshWidgets().
  function buildQuickViewReviews(product) {
    if (!product) return null;
    const wrap = document.createElement('div');
    wrap.className = 'ctw-qv-reviews';
    const w = document.createElement('div');
    w.className = 'yotpo yotpo-main-widget';
    w.setAttribute('data-product-id', product.id);
    w.setAttribute('data-name', product.title || '');
    const url = product.url || ('/products/' + product.handle);
    w.setAttribute('data-url', /^https?:/i.test(url) ? url : (window.location.origin + url));
    if (product.image) w.setAttribute('data-image-url', product.image);
    if (product.price != null) w.setAttribute('data-price', (product.price / 100).toFixed(2));
    w.setAttribute('data-currency', (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD');
    wrap.appendChild(w);
    return wrap;
  }

  const quickViewCache = new Map();
  function bindQuickView() {
    const modal = $('#ctw-quickview');
    const body = $('#ctw-quickview-body');
    const loading = $('#ctw-quickview-loading');
    if (!modal || !body) return;

    // Lightbox for gallery thumbnail clicks
    const lightbox = $('#ctw-lightbox');
    const lightboxImg = $('#ctw-lightbox-img');
    function openLightbox(src, alt) {
      if (!lightbox || !lightboxImg) return;
      lightboxImg.src = src;
      lightboxImg.alt = alt || '';
      lightbox.hidden = false;
      lightbox.setAttribute('aria-hidden', 'false');
    }
    function closeLightbox() {
      if (!lightbox) return;
      lightbox.hidden = true;
      lightbox.setAttribute('aria-hidden', 'true');
      if (lightboxImg) lightboxImg.src = '';
    }
    if (lightbox) {
      lightbox.addEventListener('click', closeLightbox);
      const closeBtn = $('#ctw-lightbox-close');
      if (closeBtn) closeBtn.addEventListener('click', (e) => { e.stopPropagation(); closeLightbox(); });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !lightbox.hidden) closeLightbox();
      });
    }

    // Intercept clicks on gallery anchors whose href is clearly an image file
    // (the gallery wraps each thumb in an <a href="cdn.shopify.com/.../image.jpg">).
    // Everything else — PDF data sheets, other product links, videos — falls
    // through and behaves natively.
    body.addEventListener('click', (e) => {
      const a = e.target.closest('a');
      if (!a || !a.href) return;
      if (/\.(jpe?g|png|webp|gif|avif)(\?|$|#)/i.test(a.href)) {
        e.preventDefault();
        const img = a.querySelector('img');
        openLightbox(a.href, img ? img.alt : '');
      }
    });

    function open(handle, section) {
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      requestAnimationFrame(() => modal.classList.add('is-open'));
      document.documentElement.classList.add('ctw-quickview-locked');
      loading.hidden = false;
      body.innerHTML = '';
      // Set the title in the header from our local product data — fast, no wait for fetch
      const titleEl = $('#ctw-quickview-title');
      const product = PRODUCTS.find(p => p.handle === handle);
      if (titleEl) titleEl.textContent = product ? product.title : 'Loading…';

      const inject = (html) => {
        const fragment = document.createRange().createContextualFragment(html);
        // Prefer the regular main-product section; fall back to the quick-view template node.
        const content = fragment.querySelector('[data-section-type="product"]') ||
                        fragment.querySelector('[data-section-type="main-product"]');
        loading.hidden = true;
        if (content) {
          body.innerHTML = '';
          // Gallery + add bar share a flex ROW so the sticky add bar is bounded
          // to the gallery's height: it scrolls with the gallery and stops
          // before the full-width description + reviews rows below, instead of
          // floating over them (the sticky containing block is the row, not the
          // whole scrolling body). On mobile the row is `display:block`, so they
          // stack gallery → add. Our deferred add-to-cart bar (title, SKU, Yotpo
          // rating, price, qty prefilled from the wizard, Add → mini-cart); the
          // theme's native buy form stays hidden.
          // 2-column grid: gallery + description share the LEFT column (so they
          // align exactly), the add bar is the RIGHT column spanning both rows
          // and sticky — bounded to the grid, it stays pinned through the
          // gallery AND description and releases at the reviews (which live
          // outside the grid, full-width below). On mobile the grid collapses to
          // a block and the children stack in DOM order: gallery → add →
          // description. Reviews are appended to the body, after the grid.
          const row = document.createElement('div');
          row.className = 'ctw-qv-row';
          row.appendChild(content);
          const addBar = buildQuickViewAdd(product, section, close);
          if (addBar) row.appendChild(addBar);
          const descEl = content.querySelector('.product-block-list__item--description');
          if (descEl) row.appendChild(descEl);
          body.appendChild(row);
          // Full reviews widget at the bottom (full-width row below everything).
          const reviews = buildQuickViewReviews(product);
          if (reviews) body.appendChild(reviews);
          // Star rating in the add bar → scroll to the reviews. Capture phase +
          // stopPropagation so Yotpo's own (window-based) scroll handler doesn't
          // run — its scroll wouldn't move our modal's scroll container.
          if (addBar && reviews) {
            const stars = addBar.querySelector('.yotpo.bottomLine');
            if (stars) {
              stars.style.cursor = 'pointer';
              stars.addEventListener('click', (ev) => {
                ev.preventDefault();
                ev.stopPropagation();
                // Scroll ONLY the modal body. scrollIntoView() walks every
                // scrollable ancestor and was nudging the panel, clipping the
                // fixed header (the X bar) out of view.
                const target = reviews.getBoundingClientRect().top - body.getBoundingClientRect().top + body.scrollTop;
                body.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
              }, true);
            }
          }
          // Nudge the theme to re-initialize any non-custom-element scripts on
          // the injected content (custom elements auto-upgrade on insertion).
          try {
            document.dispatchEvent(new CustomEvent('shopify:section:load', {
              bubbles: true,
              detail: { sectionId: content.getAttribute('data-section-id') || 'ctw-quickview', target: content }
            }));
          } catch (err) { /* non-fatal */ }
          refreshYotpo(); // render the star-rating + reviews widgets
        } else {
          body.innerHTML = '<div style="padding:24px">Sorry, we could not load this product. <a href="/products/' + escapeHtml(handle) + '" target="_blank" rel="noopener">Open product page</a>.</div>';
        }
      };

      if (quickViewCache.has(handle)) {
        inject(quickViewCache.get(handle));
        return;
      }
      fetch('/products/' + encodeURIComponent(handle) + '?view=ctw', { credentials: 'same-origin' })
        .then(r => r.ok ? r.text() : Promise.reject(r.status))
        .then(html => { quickViewCache.set(handle, html); inject(html); })
        .catch(err => {
          loading.hidden = true;
          body.innerHTML = '<div style="padding:24px;color:var(--error-color)">Could not load product. <a href="/products/' + escapeHtml(handle) + '" target="_blank" rel="noopener">Open product page</a>.</div>';
          console.error('[CTW] quickview fetch failed', err);
        });
    }

    function close() {
      modal.classList.remove('is-open');
      document.documentElement.classList.remove('ctw-quickview-locked');
      setTimeout(() => {
        modal.hidden = true;
        modal.setAttribute('aria-hidden', 'true');
        body.innerHTML = '';
      }, 250);
    }

    // Intercept clicks on product image / title anchors → open modal
    root.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-ctw-quickview-open]');
      if (!trigger) return;
      // Let middle-click / cmd-click / ctrl-click open in a new tab normally
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
      e.preventDefault();
      const handle = trigger.getAttribute('data-ctw-quickview-open');
      const sectionEl = trigger.closest('.ctw-section');
      const section = sectionEl ? sectionEl.dataset.section : null;
      open(handle, section);
    });

    // Close events
    modal.addEventListener('click', (e) => {
      if (e.target.closest('[data-ctw-quickview-close]')) close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !modal.hidden) close();
    });
  }

  // Listen for theme cart drawer changes (so external add-to-cart actions also refresh our panel)
  function bindExternalCartEvents() {
    document.addEventListener('product:added', async () => {
      if (flushInProgress()) return; // our own flush will reconcile when done
      if (Date.now() < _suppressRerenderUntil) return; // echo of our own refresh
      await fetchCart(); // updates serverCart; merged view keeps local picks overlaid
      renderCartPanel();
      renderAllSections();
    });

    // The theme re-renders its mini-cart / cart page by dispatching
    // `cart:rerendered`. This fires whenever the customer removes a line or
    // changes a quantity in the SYSTEM cart drawer — so we re-sync our own
    // panel from the authoritative server cart. The mini-cart variant of this
    // event does NOT bubble, so we listen in the capture phase to catch it.
    let _rerenderSyncTimer = null;
    document.addEventListener('cart:rerendered', () => {
      // Don't fight an add that's still in flight — let it settle first so we
      // don't fetch a mid-update cart. (fetchCart does not re-dispatch this
      // event, so there is no feedback loop.)
      if (_rerenderSyncTimer) clearTimeout(_rerenderSyncTimer);
      _rerenderSyncTimer = setTimeout(async () => {
        _rerenderSyncTimer = null;
        if (flushInProgress()) return; // a flush is mid-flight; it reconciles when done
        if (Date.now() < _suppressRerenderUntil) return; // echo of our own refresh — skip the redundant read
        await fetchCart(); // updates serverCart; merged view keeps local picks overlaid
        renderCartPanel();
        renderAllSections();
      }, 200);
    }, true);
  }

  // -------------------- Init --------------------
  async function init() {
    loadState();
    bindQuestionInputs();
    bindProductActions();
    bindCartToggle();
    bindFloatingCartTouchFix();
    bindReset();
    bindTooltipsMobile();
    bindQuickView();
    bindExternalCartEvents();
    bindFlushBeforeUnload();
    await fetchCart();
    // ---- Restore the local pending picks (the deferred queue is long-lived) ----
    // If the previous view marked a flush attempt on its way out (pagehide
    // keepalive POST), compare the fresh server cart against the snapshot taken
    // at that moment: items that landed are dropped, items that didn't stay
    // pending — no loss, no double-adds.
    //
    // NEVER inside the theme editor or any iframe: pending picks and redirects
    // in the editor's preview frame make the editor refuse to load the page.
    // Keys are left untouched so a real storefront visit still restores them.
    let embedded = !!(window.Shopify && window.Shopify.designMode);
    try { embedded = embedded || window.top !== window.self; } catch (e) { embedded = true; }
    if (!embedded) {
      const saved = loadPersistedPending();
      if (saved) {
        let items = saved.items;
        if (saved.flushedAt) {
          const nowCounts = serverVariantCounts();
          items = items.filter(it =>
            (nowCounts[it.id] || 0) < ((saved.serverCounts[it.id] || 0) + it.quantity)
          );
        }
        if (items.length) {
          console.info('[CTW] restored', items.length, 'pending pick(s)');
          _addQueue = items;
        }
        persistPendingAdds(); // rewrite in normal (un-flushed) form, or clear
        recomputeMergedCart();
      }
    }
    renderAllSections();
    renderCartPanel();
    if (!embedded) {
      // If the customer was headed to the cart/checkout when a challenge
      // interrupted, flush now (the reload solved the challenge) and continue
      // to where they clicked — with all their items.
      const resumeNav = takeResumeNav();
      if (resumeNav && _addQueue.length) {
        await settleCartAdds();
        // If the flush got challenged again, stay here — the banner explains.
        if (!_cartLockedOut) window.location.href = resumeNav;
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
